import { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { type CompleteAnalysis } from '../api/types'
import { savedDeckFields } from '../lib/savedDeckFields'
import { isAnalysisVersionCompatible } from '../api/analysisVersion'
import { isAnalysisInOtherLanguage } from '../lib/analysisLocale'
import { supabase } from '../lib/supabase'
import { deckBusy } from '../lib/watchtower'
import { DECK_BUSY_RETRY_MS, rejectionAsError, withDeckBusyRetry } from '../lib/deckBusyRetry'
import { tc } from '../i18n'
import { analysisContentHash, countDecklistCards, frontKey } from '../lib/decklist'
import { canonicalCardMeta, mergeCardMetaDelta, cardMetaTooLarge, parseCardMeta, pruneCardMetaForText, storedCardMeta, type CardMetaMap } from '../lib/cardMeta'
import { rekeyMigratedTextDrafts } from '../components/decks/editor/textDraftStore'
import { broadcastPersisted, pendingGuardedWrites, pruneDraftsForMissingDecks, rekeyMigratedDrafts, subscribePersistedDecks, trackGuardedWrite } from '../components/decks/editor/editorSaveQueue'
import { useAuth } from './useAuth'
import { invalidateBuiltDecks } from '../lib/builtDecksSignal'
import { onSavedDecksChangedRemotely } from '../lib/savedDecksRemoteSignal'
import { invalidateDeckDependentCollectionData, noteSavedDecksLoaded, noteSavedDecksWritten, noteServerDeckRow } from '../lib/deckDependentCollection'
import { setRefreshChoice } from '../lib/linkedRefresh'
import { revOf, rowHoldsPayload as rowHoldsPayloadShared } from '../lib/deckRowPayload'
import { offlineRuntimeActive, readOwnerDecks, storeDeckSnapshot } from '../lib/offline/runtime'
import { getIdentity, onIdentityEmit } from '../lib/offline/identity'
import { onConnectivityEvent } from '../lib/offline/connectivity'
import { CONN_EVENT, IDENTITY_EMIT, MODE } from '../lib/offline/offline.codes'
import { recordMigratedIds } from '../lib/finishReturn'
import { markDraft } from '../lib/deckDraft'

// Bumped to 2026-05-15 with the lock-state predictor flag flip
// (LOCK_STATE_WARP_V1 ON in prod since 2026-05-14, schema 5.1.0).
// Triggers re-analyze prompts so saved decks pick up lock-state signals
// and the new bracket_narrative wording.
export const CURRENT_ANALYSIS_VERSION = '2026-05-15'

// Ownership splits the Decks hub: 'owned' = My Decks (built by you), 'saved' =
// analyzed/imported decks you don't own. NULL/undefined defaults to 'owned'
// (see deckOwnership.segmentOf) so existing decks land in My Decks.
export type DeckOwnership = 'owned' | 'saved'

export interface SavedDeck {
  id: string
  commander: string | null
  partner?: string | null
  bracket: number
  engine: string
  cardCount: number
  decklist?: string
  savedAt: string // ISO date
  analysisVersion?: string
  ownership?: DeckOwnership
  // Deck naming. NULL/undefined means "render the commander", which is how
  // every deck behaved before 2026-08-04. Never write an empty string here.
  name?: string | null
  // Source link. Set only on URL-imported decks; NULL means a pasted decklist,
  // which behaves exactly as it always has.
  source?: string | null
  sourceUrl?: string | null
  sourceSyncedAt?: string | null
  // Hash of the decklist AS PULLED. Divergence is hash-vs-hash, never
  // updated_at-vs-source_synced_at: updated_at also moves on a rename or a
  // share toggle, which would falsely read as a user edit.
  sourceDecklistHash?: string | null
  // Share/Library/profile state (cloud rows only; arrives via select('*')).
  // isPublic = a share link exists; libraryListed = opted into the public
  // Library; profileFeatured = opted onto the owner's creator page at
  // /u/<token>. All three are INDEPENDENT: sharing a link for social media
  // publishes to neither surface. Must survive every deck-object
  // reconstruction (re-analyze, reclassify) or the UI shows a false
  // privacy indicator.
  isPublic?: boolean
  libraryListed?: boolean
  profileFeatured?: boolean
  publicShareToken?: string | null
  // Per-card printing / foil / tags (saved_decks.card_meta, Wave 4), read
  // leniently (lib/cardMeta parseCardMeta). Absent or null: none. Old
  // clients never send the column, so it survives their decklist-only saves.
  cardMeta?: CardMetaMap | null
  // saved_decks.rev: the row's revision, set by a database trigger (0 on
  // insert, +1 on EVERY update, a no-op or an old client's write included;
  // a value the client sends is ignored). A rev is only ever kept beside the
  // columns it was read with (a full row from the database). Absent means
  // UNKNOWN: a guest row, an older cached row, or an optimistic copy whose
  // write has not answered. A rev-guarded save with no known rev reads the
  // row fresh first.
  rev?: number
  analysis: CompleteAnalysis
}

/**
 * A rev-guarded write whose response was lost (a thrown fetch, no status, a
 * 5xx; never a 408 / 429): it may or may not have committed. Kept EXACTLY as
 * sent, guard included, IN MEMORY only, and only ever resolved by
 * saveDeckChecked(..., { resolveUnknown }): an identical retry while the row
 * is still at its rev, never a write against a newer one. (A reload does not
 * carry it: the editor's draft is marked uncertain instead.)
 */
export interface UnknownSaveAttempt {
  deckId: string
  /** The rev guard N the attempt was sent with (`rev=eq.N`). */
  rev: number
  /** Its decklist guard, when it had one (null: `decklist IS NULL`). */
  expectedDecklist?: string | null
  /** The exact columns it wrote. */
  payload: Record<string, unknown>
  /** A fence / forced recovery write: it only counts as saved once a
   *  revision was actually CONSUMED (the row past `rev`). The row holding the
   *  payload at `rev` itself is not success. */
  mustConsumeRev?: true
}

// saveDeck's return. cloudSynced is true only when the deck row was actually
// written to Supabase (cloud mode AND the write succeeded) - callers that need
// a real saved_decks row (e.g. /decks/share) must gate on it; the id is a
// local temp id whenever cloudSynced is false.
// `isCloud` is the session's mode at save time (signed in AND Supabase
// configured) - NOT the same thing as cloudSynced. It exists so a caller can
// tell "guest, local-only save, working exactly as designed" (isCloud false,
// cloudSynced false) apart from "signed-in user whose cloud write failed"
// (isCloud true, cloudSynced false) - the two read identically on cloudSynced
// alone, and conflating them is what let a failed cloud save during a bulk
// import report itself as a success (Codex round 1, Blocker B).
export interface SaveDeckResult {
  id: string
  cloudSynced: boolean
  isCloud: boolean
  // True only in local-only mode (a guest) when the deck actually reached
  // localStorage. saveDeck resolves either way (a full or blocked storage
  // keeps the deck for the session only), so a caller that is about to
  // navigate away, e.g. into sign-in, must gate on this: a deck that only
  // lives in React state is gone the moment the page unloads. Always false
  // in cloud mode, where localStorage is deliberately not written.
  persistedLocally: boolean
  // Set only when a guarded save (updateOnly / expectedDecklist) wrote NOTHING
  // anywhere: 'missing' = the existingId row is not in the list (deleted
  // meanwhile), 'stale' = the row's decklist moved on since the caller's edit
  // started. cloudSynced and persistedLocally are false alongside it.
  refused?: 'missing' | 'stale'
}

/** Why a write can never land by retrying it as is. */
export type SaveFatalCode = 'auth' | 'invalid' | 'storage_full' | 'storage'

/**
 * saveDeckChecked's typed result. ONE of:
 *  - saved: the write landed (or had already landed: a lost response, or
 *    another tab writing the identical text). `deck` is the STORED row - the
 *    database row in cloud mode, the object written to localStorage for a
 *    guest - never a reconstruction by the caller.
 *  - stale: the compare-and-swap refused: the row moved past the rev this
 *    edit was based on, and does not hold exactly what this save writes.
 *  - missing: the row is gone (or never existed). Nothing was written.
 *  - unknown: a rev-guarded write's response was lost and a fresh read could
 *    not settle whether it landed. Resolve `attempt` (resolveUnknown) first.
 *  - retryable_error: the write did not run (a 408 / 429, an unguarded
 *    network failure). Try again later.
 *  - fatal_error: retrying cannot help (signed out, a rejected payload, full
 *    or blocked storage).
 */
export type SaveOutcome =
  // cardMetaHeld: the list landed but the card_meta part did NOT (its guard
  // was rejected, so it was never sent). The caller keeps it pending.
  | { status: 'saved'; deck: SavedDeck; cardMetaHeld?: true }
  // The write may or may not have landed (see UnknownSaveAttempt). The caller
  // holds `attempt` and resolves it before writing anything newer.
  // fatalCode: the identical retry was REJECTED (signed out, ...). The
  // attempt is still unknown (it may have committed before), but retrying on
  // its own cannot help: the caller stops until asked, keeping it.
  | { status: 'unknown'; attempt: UnknownSaveAttempt; cause: unknown; fatalCode?: SaveFatalCode }
  | { status: 'stale' }
  | { status: 'missing' }
  | { status: 'retryable_error'; cause: unknown }
  | { status: 'fatal_error'; code: SaveFatalCode; cause: unknown }

// Shared signature for saveDeck so prop types can't drift from the hook.
// `forceNew` bypasses the commander+partner dedup so a copy (Library/precon ->
// your decks) always lands in its OWN row. Without it, saving a copy of a
// precon whose commander you already play would overwrite your existing deck.
export type SaveDeckFn = (
  analysis: CompleteAnalysis,
  decklistText?: string,
  partnerName?: string | null,
  opts?: SaveDeckOptions,
) => Promise<SaveDeckResult>

/** The same write as SaveDeckFn with a typed outcome (see SaveOutcome). Only
 *  the deck editor's save queue and its re-rate use it; every other caller
 *  keeps saveDeck's contract. */
export type SaveDeckCheckedFn = (
  analysis: CompleteAnalysis,
  decklistText: string | undefined,
  partnerName: string | null | undefined,
  opts: CheckedSaveOptions,
) => Promise<SaveOutcome>

/**
 * A checked save MUST say which revision it is based on: the rev the caller
 * read WITH its base list, captured before its edit or analysis started (a
 * row the hook installed later is never used instead). 'fresh' = the caller
 * has none: an instant save then reads the row once and proceeds only while
 * it still holds the base list and command zone; a FULL save with 'fresh' is
 * refused as stale in cloud mode (the caller must read the base rev before
 * analyzing, see readDeckBase). A guest has no rev: either value is ignored.
 */
export type CheckedSaveOptions = SaveDeckOptions & (
  | { expectedRev: number | 'fresh'; resolveUnknown?: undefined }
  | { resolveUnknown: UnknownSaveAttempt }
)

/** The base a full save (re-rate, re-analysis) must read BEFORE analyzing,
 *  when it has no rev: the row's list and rev as stored now. */
export interface DeckBase {
  decklist: string | null
  rev: number
  commander: string | null
  partner: string | null
  /** The whole row as read (every column), for a caller that reconciles
   *  by content. */
  row?: SavedDeck
}

export interface SaveDeckOptions {
    existingId?: string
    ownership?: DeckOwnership
    forceNew?: boolean
    // Recency opt-out for BACKGROUND saves, default true. My Decks orders on
    // updated_at (see the load query below), so any save that stamps it moves
    // the deck to the top of the list. That is right for a save the user asked
    // for and wrong for a silent self-heal: merely OPENING a legacy deck fires
    // DeckDetailV2's backfill re-analyze, which used to climb that deck over
    // decks the user had actually worked on. false omits updated_at from the
    // UPDATE payload and keeps the local savedAt at the prior row's value.
    // INSERTs always stamp it - a brand-new row is always user-initiated, and
    // a row with no updated_at at all would sort to the bottom of the list.
    bumpRecency?: boolean
    // Present only for Archidekt/Moxfield/Manabox linked saves. Linked saves
    // dedup on sourceUrl instead of commander+partner, and stamp the four
    // link columns on the row - see the dedicated `link` handling in saveDeck.
    // `mode: 'refresh'` marks a re-pull of an already-linked deck (as opposed
    // to a first-time import): saveDeck reads it to decide the effective
    // `partner`, below - see that computation's own comment for why.
    link?: { sourceUrl: string; source: string; name?: string | null; decklistHash: string; mode?: 'import' | 'refresh' }
    // AUTOMATIC saves only (DeckDetailV2's silent refresh, i18n 3e Codex R2).
    // The row in `decks` is replaced optimistically, as for every save; with
    // this set, a save that did not land durably (no cloud row for a signed-in
    // user, no localStorage write in local mode) puts the prior row back, so
    // the list never shows an analysis that was not stored and the deck stays
    // due for its refresh. User-initiated saves leave it unset and keep their
    // optimistic behaviour exactly as before.
    rollbackOnFailure?: boolean
    // GUARDED saves (the deck editor's recompute, which can finish after its
    // screen unmounted). Both default off, so every other caller is unchanged.
    //
    // updateOnly: the existingId row must still be in the CURRENT list. If it
    // is not (the user deleted the deck while the analysis ran), nothing is
    // written: no optimistic row, no localStorage insert, no cloud write. A
    // plain existingId save re-inserts a missing row, which resurrected a deck
    // the user had just removed.
    updateOnly?: boolean
    // Compare-and-swap on the stored list: the decklist text (null when the row
    // has none) the caller's edit started from. A row whose decklist differs
    // was changed by a newer save, so this one stands down instead of writing
    // an older edit over it. Checked against the CURRENT list, never a caller's
    // stale snapshot. With updateOnly in cloud mode the UPDATE itself carries
    // the condition (`decklist = expected`), so a second tab's write is seen
    // too, not only this hook's own list.
    expectedDecklist?: string | null
    // The deck editor's INSTANT save: write the list without re-analyzing.
    // Requires an existing row (refused as 'missing' otherwise) and touches
    // ONLY decklist and card_count: commander, partner, bracket, engine, the
    // analysis and its version, link / share / name / ownership state and
    // recency (updated_at, savedAt) all stay as they are. The analysis column
    // is NEVER sent: a legacy row with no analyzed_content_hash stays
    // unstamped until its next full save, and a newer rating another tab or
    // device wrote is never overwritten.
    keepAnalysis?: boolean
    // keepAnalysis only: the editor changed the command zone ("Set as
    // commander" / "Set as partner"), so the row's commander and partner
    // columns are written in the SAME update as the decklist (one
    // compare-and-swap: a refusal writes neither). `partner: null` clears it.
    // Absent (every other caller), the instant save keeps the row's own
    // commander and partner exactly as before. Ignored on a full save, whose
    // commander comes from the analysis and partner from `partnerName`.
    commandZone?: { commander: string | null; partner: string | null }
    // keepAnalysis only: the editor's per-card printing / foil / tags
    // (card_meta), written in the SAME compare-and-swap update as the
    // decklist (a refusal writes neither). The caller prunes it to the list;
    // an empty map is stored as null. Absent (every other caller, and every
    // old client), the column is never sent and stays exactly as stored.
    // Over CARD_META_MAX_BYTES it is refused before anything is written
    // (fatal 'invalid').
    cardMeta?: CardMetaMap | null
    // keepAnalysis only, instead of cardMeta, when the caller does NOT know
    // the row's card_meta (loaded by a path that never read the column): the
    // cards whose entry moved from `base` to `next` are applied to a FRESH
    // read of the row's card_meta, and that result (pruned to the list) is
    // written in the same compare-and-swap. An unknown value is never
    // replaced wholesale.
    cardMetaMerge?: { base: CardMetaMap; next: CardMetaMap }
    // With cardMetaMerge, from a caller that never saw the whole stored map
    // (an unknown column): the merge is applied WITHOUT pruning the fresh
    // read's entries for cards the text does not hold (it is not this
    // caller's to delete them).
    cardMetaNoPrune?: boolean
    // Guarded saves (updateOnly + expectedDecklist) in cloud mode: the row
    // revision the caller's edit is based on. The UPDATE carries `rev = N`
    // and adopts the rev it returns. See CheckedSaveOptions ('fresh').
    // Absent (an unchecked legacy saveDeck call) is treated as 'fresh'.
    expectedRev?: number | 'fresh'
    // The command zone (commander / partner columns) the caller's edit is
    // based on, beside expectedDecklist. An instant save that moves the zone
    // and finds the row past its base rev compares the row to THIS; absent,
    // such a save is stale.
    expectedCommandZone?: { commander: string | null; partner: string | null }
    // A FENCE or forced recovery write (the deck editor's queue): its point
    // is to consume the base revision so an older write still in flight can
    // no longer land. Saved ONLY when an update actually committed (the
    // PATCH returned the row, or a re-read finds the row past the base rev
    // holding it); never by the "row already holds it" shortcut, and never
    // without a rev (one is read first).
    mustConsumeRev?: boolean
    // keepAnalysis only, with mustConsumeRev: the no-op fence itself. The
    // PATCH writes the decklist column to the value expectedDecklist says it
    // holds (null included, so a legacy list-less row is never given a
    // synthesized list); the trigger bumps rev. Nothing else is written.
    fenceOnly?: boolean
    // Resolve a lost write (an 'unknown' outcome) instead of saving: every
    // content argument is ignored. Saved = it landed (or an identical retry
    // against the SAME rev did), stale = the row moved past it, unknown =
    // still unsettled. Never writes against any rev but the attempt's own.
    resolveUnknown?: UnknownSaveAttempt
    // The storage scope (the account id, or 'guest') the caller STARTED in,
    // read through currentScope() before its first await. Checked against
    // the writer that would run the save NOW: a session that changed
    // meanwhile (signed out, signed in, another account) is refused as
    // 'stale' with nothing written anywhere, so an account's deck never
    // lands in guest storage and a guest's never in an account.
    expectedScope?: string
}

// A pulled list an unguarded link save could not write (see
// linkedImportConflict), held for the user's choice.
interface LinkedPending {
  analysis: CompleteAnalysis
  decklist: string
  partner: string | null | undefined
  link: NonNullable<SaveDeckOptions['link']>
}

/**
 * The columns a FULL save writes (the one definition: the write itself, the
 * "already landed" check of an unguarded link save, and the refresh
 * confirmation all compare against it with rowHoldsPayload).
 */
function fullSavePayload(f: {
  userId: string
  commander: string | null
  partner: string | null
  bracket: number
  engine: string
  cardCount: number
  decklist: string | undefined
  analysis: CompleteAnalysis
  bumpRecency: boolean
  ownership?: DeckOwnership
  link?: SaveDeckOptions['link']
}): Record<string, unknown> {
  return {
    user_id: f.userId,
    commander: f.commander,
    partner: f.partner,
    bracket: f.bracket,
    engine: f.engine,
    card_count: f.cardCount,
    decklist: f.decklist ?? null,
    analysis: f.analysis as unknown as Record<string, unknown>,
    // Recency: omitted entirely on a background save (bumpRecency false)
    // so the row keeps whatever updated_at it already had and stays where
    // the user left it in My Decks. Every other save stamps it, exactly
    // as before.
    ...(f.bumpRecency ? { updated_at: new Date().toISOString() } : {}),
    // Only send ownership when the caller set it, so callers that don't
    // (re-analyze, legacy saves) never touch the column - and a build/eval
    // where the column isn't applied yet can't 400 on the write.
    ...(f.ownership ? { ownership: f.ownership } : {}),
    // Only send the link columns when the caller supplies opts.link
    // (import or refresh), so every other save path never touches them.
    // The `name` spread is itself conditional: a refresh must never
    // overwrite a name the user has since set. Only write `name` when
    // the caller supplies one, which the import path does and the
    // refresh path does not.
    ...(f.link ? {
      source: f.link.source,
      source_url: f.link.sourceUrl,
      source_synced_at: new Date().toISOString(),
      source_decklist_hash: f.link.decklistHash,
      ...(f.link.name ? { name: f.link.name } : {}),
    } : {}),
  }
}

/** A SavedDeck as the row columns a full save compares (rowHoldsPayload). */
function deckAsRow(d: SavedDeck, userId: string): Partial<DbRow> {
  return {
    id: d.id, user_id: userId, commander: d.commander, partner: d.partner ?? null, bracket: d.bracket, engine: d.engine,
    card_count: d.cardCount, decklist: d.decklist ?? null, analysis: d.analysis, ownership: d.ownership ?? null,
    name: d.name ?? null, source: d.source ?? null, source_url: d.sourceUrl ?? null,
    source_decklist_hash: d.sourceDecklistHash ?? null,
  } as Partial<DbRow>
}

// Exported for the universal search's names-only deck index (src/lib/deckNameIndex.ts).
export const SAVED_DECKS_STORAGE_KEY = 'commander-bracket-saved-decks'
const STORAGE_KEY = SAVED_DECKS_STORAGE_KEY
const MIGRATION_KEY = 'commander-bracket-migrated'

// --- localStorage helpers ---

function loadLocalDecks(): SavedDeck[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    return JSON.parse(raw) as SavedDeck[]
  } catch {
    return []
  }
}

// THE one writer of the saved list in localStorage. Every guest change
// reaches it either through mutateGuestDecks or a content save's own locked
// read / compare / write. It says WHY a write failed: a full store (QuotaExceededError) or
// a blocked one (SecurityError, private modes). null = written.
function writeLocalDecks(decks: SavedDeck[]): { code: 'storage_full' | 'storage'; cause: unknown } | null {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(decks))
    return null
  } catch (e) {
    console.error('Failed to persist decks to localStorage:', e)
    const name = (e as { name?: string } | null)?.name
    // eslint-disable-next-line i18next/no-literal-string -- storage failure codes
    return { code: name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED' ? 'storage_full' : 'storage', cause: e }
  }
}

// The stored list, or null when storage cannot be READ at all (blocked). A
// missing key or bad JSON is an empty list, as in loadLocalDecks.
// null = NOT a list this code may act on: storage that cannot be read, or a
// stored value that does not parse or is not an array. Callers then neither
// write (a malformed store is logged and left alone, never overwritten) nor
// prune drafts against it. A missing key is a valid empty list.
function readLocalDecksStrict(): SavedDeck[] | null {
  let raw: string | null
  try {
    raw = localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (Array.isArray(parsed)) return parsed as SavedDeck[]
    console.error('Saved decks in localStorage are not a list; leaving them untouched.')
    return null
  } catch (e) {
    console.error('Saved decks in localStorage do not parse; leaving them untouched.', e)
    return null
  }
}

/**
 * A cloud write error, sorted into "try again later" and "retrying cannot
 * help". `status` is PostgREST's HTTP status when the client had one.
 */
function classifyCloudError(
  error: { message?: string; code?: string } | null | undefined,
  status: number | undefined,
): { kind: 'retryable' } | { kind: 'fatal'; code: SaveFatalCode } {
  /* eslint-disable i18next/no-literal-string -- error kinds and codes */
  const message = error?.message ?? ''
  const code = error?.code ?? ''
  if (status === 408 || status === 429 || (status != null && status >= 500)) return { kind: 'retryable' }
  if (status === 401 || status === 403 || code === 'PGRST301' || code === 'PGRST302' || /jwt|not signed in/i.test(message)) {
    return { kind: 'fatal', code: 'auth' }
  }
  if (status != null && status >= 400) return { kind: 'fatal', code: 'invalid' }
  // No HTTP status: a network failure, a timeout or an abort, unless the
  // error carries a PostgREST / Postgres validation code.
  if (/^(22|23|42|PGRST1)/.test(code)) return { kind: 'fatal', code: 'invalid' }
  return { kind: 'retryable' }
  /* eslint-enable i18next/no-literal-string */
}

/**
 * A failed write whose OUTCOME is unknown: it may have committed. A thrown
 * fetch, no HTTP status, or a 5xx from something in front of the database.
 * NOT a 408 / 429 (the request was refused before it ran) and not an answer
 * that carries a PostgREST or Postgres error code (the database answered,
 * and a failed statement rolled back).
 */
function isAmbiguousFailure(error: { code?: string } | null | undefined, status: number | undefined): boolean {
  if (status === 408 || status === 429) return false
  const code = error?.code ?? ''
  // A SQLSTATE (five characters, e.g. 55P03 deck_busy) or a PGRST code: the
  // database itself answered.
  if (/^PGRST/.test(code) || /^[0-9A-Z]{5}$/.test(code)) return false
  if (status != null && status >= 500) return true
  if (status != null && status > 0) return false
  return true
}

// revOf, stableJson and rowHoldsPayload live in lib/deckRowPayload (shared
// with the offline deck outbox, which settles lost writes the same way).
function rowHoldsPayload(row: Partial<DbRow>, payload: Record<string, unknown>): boolean {
  return rowHoldsPayloadShared(row as Record<string, unknown>, payload)
}

// The stored row a write produced: the cloud outcome's row, or the guest row
// written to storage. null when nothing landed.
function outcomePreview(cloud: SaveOutcome | null, persistedLocally: boolean, written: SavedDeck): SavedDeck | null {
  if (cloud) return cloud.status === 'saved' ? cloud.deck : null
  return persistedLocally ? written : null
}

// mapRowToDeck's field -> column table, so a partial row (a select that did
// not return a column) falls back to the locally written value for exactly
// the columns it lacks.
const ROW_COLUMNS: [keyof SavedDeck, keyof DbRow][] = [
  /* eslint-disable i18next/no-literal-string -- field and column names */
  ['commander', 'commander'], ['partner', 'partner'], ['bracket', 'bracket'], ['engine', 'engine'],
  ['cardCount', 'card_count'], ['decklist', 'decklist'], ['savedAt', 'updated_at'], ['ownership', 'ownership'],
  ['isPublic', 'is_public'], ['libraryListed', 'library_listed'], ['profileFeatured', 'profile_featured'],
  ['publicShareToken', 'public_share_token'], ['name', 'name'], ['source', 'source'], ['sourceUrl', 'source_url'],
  ['sourceSyncedAt', 'source_synced_at'], ['sourceDecklistHash', 'source_decklist_hash'], ['analysis', 'analysis'],
  ['cardMeta', 'card_meta'],
  /* eslint-enable i18next/no-literal-string */
]

/**
 * The SavedDeck for a row the database returned, merging in only what the
 * row cannot carry: the local-only `analysisVersion`, and any column the
 * select did not return.
 */
function deckFromDbRow(row: Partial<DbRow>, local: SavedDeck): SavedDeck {
  const mapped = mapRowToDeck({ ...(row as DbRow), created_at: row.created_at ?? local.savedAt })
  const out: SavedDeck = { ...mapped, id: row.id ?? local.id, analysisVersion: local.analysisVersion }
  const writable = out as unknown as Record<string, unknown>
  for (const [field, col] of ROW_COLUMNS) {
    if (!(col in row)) writable[field] = (local as unknown as Record<string, unknown>)[field]
  }
  // A rev describes the columns it was read WITH: never kept beside a local
  // fallback for the list a rev-guarded save is based on. (card_meta is
  // never trusted from a cached row: every card_meta write merges onto a
  // fresh read.)
  // eslint-disable-next-line i18next/no-literal-string -- a column name
  if (!('decklist' in row)) delete out.rev
  return out
}

// --- Supabase helpers ---

interface DbRow {
  id: string
  commander: string | null
  // Optional (not just nullable): the round-trip test constructs a row
  // literal without it, matching a real Supabase select('*') row shape where
  // a column can simply be absent from the JS object in edge cases (e.g. a
  // partial select). mapRowToDeck already handles it with `row.partner ?? null`.
  partner?: string | null
  bracket: number
  engine: string
  card_count: number
  decklist: string | null
  analysis: CompleteAnalysis
  created_at: string
  // Nullable + optional: attemptCloudSync stamps updated_at on every save, but
  // rows written before that column existed (or via other clients) can carry
  // NULL, and a partial select can omit it entirely. mapRowToDeck falls back
  // to created_at.
  updated_at?: string | null
  ownership?: string | null
  is_public?: boolean | null
  library_listed?: boolean | null
  profile_featured?: boolean | null
  public_share_token?: string | null
  name?: string | null
  source?: string | null
  source_url?: string | null
  source_synced_at?: string | null
  source_decklist_hash?: string | null
  card_meta?: unknown
  rev?: number | string | null
}

// Exported so the round-trip contract (cloud row -> SavedDeck) can be tested
// directly without spinning up the hook's cloud-mode plumbing.
export function mapRowToDeck(row: DbRow): SavedDeck {
  return {
    id: row.id,
    commander: row.commander,
    partner: row.partner ?? null,
    bracket: row.bracket,
    engine: row.engine,
    cardCount: row.card_count,
    decklist: row.decklist ?? undefined,
    // Last-touched wins: a deck updated yesterday should read as yesterday's
    // deck, not as the day it was first created. created_at is the fallback
    // for legacy rows with a NULL updated_at.
    savedAt: row.updated_at ?? row.created_at,
    // Column is additive + nullable; absent on older rows -> undefined -> My Decks.
    // eslint-disable-next-line i18next/no-literal-string -- stored identity code (ownership, engine)
    ownership: row.ownership === 'saved' ? 'saved' : row.ownership === 'owned' ? 'owned' : undefined,
    isPublic: row.is_public ?? undefined,
    libraryListed: row.library_listed ?? undefined,
    profileFeatured: row.profile_featured ?? undefined,
    publicShareToken: row.public_share_token ?? undefined,
    name: row.name ?? undefined,
    source: row.source ?? undefined,
    sourceUrl: row.source_url ?? undefined,
    sourceSyncedAt: row.source_synced_at ?? undefined,
    sourceDecklistHash: row.source_decklist_hash ?? undefined,
    // Lenient: a malformed value or entry is dropped, never thrown on. Only
    // set when the row carries entries, so a row without the column maps
    // exactly as before.
    // A row that carries the column says what it holds (null: none); one
    // that does not (a partial select) leaves it UNKNOWN (absent).
    ...('card_meta' in row ? { cardMeta: canonicalCardMeta(parseCardMeta(row.card_meta)) ? parseCardMeta(row.card_meta) : null } : {}),
    // Only a row that carries it (select('*') after the column landed).
    ...(revOf(row) !== undefined ? { rev: revOf(row) } : {}),
    analysis: row.analysis,
  }
}

// --- Shared ---

// Moved to lib/savedDeckFields (shared with the deck-box runner); re-exported
// for every existing importer.
export { extractCommander } from '../lib/savedDeckFields'

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 6)
}

// The fields a content save (saveDeck) writes from the analysis + decklist, as
// opposed to row metadata (name, ownership, share and link state) that other
// mutations own.
function contentFields(d: SavedDeck): Pick<SavedDeck,
  'commander' | 'partner' | 'bracket' | 'engine' | 'cardCount' | 'decklist' | 'savedAt' | 'analysisVersion' | 'analysis' | 'cardMeta' | 'rev'> {
  return {
    // The rev of the content a rollback puts back (it describes exactly that).
    rev: d.rev,
    // card_meta rides an instant save with the list (Wave 4), so a rollback
    // puts it back with the list.
    cardMeta: d.cardMeta,
    commander: d.commander,
    partner: d.partner,
    bracket: d.bracket,
    engine: d.engine,
    cardCount: d.cardCount,
    decklist: d.decklist,
    savedAt: d.savedAt,
    analysisVersion: d.analysisVersion,
    analysis: d.analysis,
  }
}

// --- Hook ---

export function useSavedDecks(decklist?: string) {
  const { user, loading: authLoading } = useAuth()
  const isCloud = !!user && !!supabase
  // Always start empty. Initializing from loadLocalDecks() while auth
  // was still resolving briefly leaked "phantom" local decks into the
  // signed-in My Decks view (reported by alexw4342@yahoo.com,
  // 2026-05-22). Worse, those phantoms could resurface on sign-out and
  // get pulled into cloud via the migration prompt. Wait for the
  // useEffect below to decide cloud vs. local against resolved auth.
  const [decks, setDecksState] = useState<SavedDeck[]>([])
  // The CURRENT list, updated SYNCHRONOUSLY by every mutation below, never by
  // a render or an effect. saveDeck's guards (updateOnly, expectedDecklist)
  // read it: a passive-effect copy lagged a newer save, so an older
  // continuation running before the effect compare-and-swapped against the
  // old list, passed, and overwrote the newer one.
  const decksRef = useRef<SavedDeck[]>([])
  // Each load's token: a load whose token is no longer the newest finished
  // out of order and its rows are DISCARDED.
  const loadSeqRef = useRef(0)
  const loadInFlightRef = useRef(false)
  const reloadRef = useRef<((background?: boolean) => void) | null>(null)
  // MUTATION EPOCH: bumped by everything that changes what this list should
  // hold behind a load's back (a guarded write landing, this instance's own
  // included; a broadcast from another instance; a delete; a stale-refusal
  // reconciliation). A load that completes under a different epoch than it
  // started with never installs its rows: it hands over to ONE trailing
  // reload (coalesced: one active load, at most one more queued behind it).
  const mutationEpochRef = useRef(0)
  const bumpEpoch = () => { mutationEpochRef.current += 1 }
  // Re-read now unless a load is out; that load re-runs once when it sees the
  // epoch moved (never more than one active plus one queued).
  const requestReload = () => { if (!loadInFlightRef.current) reloadRef.current?.(true) }
  // This instance, so a broadcast of its own landed write is not re-applied.
  const instanceIdRef = useRef<object>({})
  // The ONE way to change the list: `next` is computed from decksRef (not
  // React's queued state), the ref is assigned, then React is told. Same call
  // shape as a useState setter, so every call site reads as before.
  // EVERY non-load change to the list goes through here, and bumps the
  // mutation epoch: a load that is out when it happens re-reads instead of
  // installing its older rows. Loads install through installLoadedDecks.
  const setDecks = useCallback((next: SavedDeck[] | ((prev: SavedDeck[]) => SavedDeck[])) => {
    mutationEpochRef.current += 1
    const value = typeof next === 'function' ? next(decksRef.current) : next
    decksRef.current = value
    setDecksState(value)
  }, [])
  const installLoadedDecks = useCallback((list: SavedDeck[]) => {
    decksRef.current = list
    setDecksState(list)
  }, [])
  // My Decks changed by THIS tab's own successful write (a save, delete,
  // ownership change): drop the deck-dependent collection data, and record
  // the list as it now stands, so the next load does not count the same
  // change as a remote one.
  const decksChangedRef = useRef((ownerId: string) => {
    invalidateDeckDependentCollectionData()
    noteSavedDecksWritten(ownerId, decksRef.current)
  })
  /**
   * Guest mode's ONE read / modify / write of the saved list (every guest
   * list change besides a content save, which runs the same steps inside its
   * own lock): under the 'cb-saved-decks' Web Lock where the browser has it,
   * read storage FRESH, apply `fn`, write once, then show exactly the list
   * written (bumping the epoch). `fn` returning null changes nothing.
   * Resolves false when storage could not be read or written.
   */
  const mutateGuestDecks = useCallback(async (fn: (fresh: SavedDeck[]) => SavedDeck[] | null): Promise<boolean> => {
    const run = async () => {
      const fresh = readLocalDecksStrict()
      if (fresh === null) return false
      const next = fn(fresh)
      if (next === null) return true
      if (writeLocalDecks(next) !== null) return false
      setDecks(next)
      return true
    }
    const locks = typeof navigator !== 'undefined'
      ? (navigator as Navigator & { locks?: { request: (name: string, cb: () => Promise<boolean>) => Promise<boolean> } }).locks
      : undefined
    // eslint-disable-next-line i18next/no-literal-string -- a Web Lock name
    return locks?.request ? locks.request('cb-saved-decks', run) : run()
  }, [setDecks])

    // The latest CONTENT save attempt per row id, so a failed save rolls back
  // only while it is still the newest attempt on that row (see rollback).
  const contentAttemptRef = useRef(new Map<string, number>())
  const contentAttemptSeq = useRef(0)
  // Hold loading=true until auth resolves AND the first load (cloud
  // or local) completes. The Decks hub uses this to suppress empty
  // states during the brief auth-resolve window.
  const [loading, setLoading] = useState(true)
  const [migrationNeeded, setMigrationNeeded] = useState(false)
  const [cloudSyncError, setCloudSyncError] = useState<string | null>(null)
  // Offline contract W3: the list shown is the copy stored on this device
  // (its last complete refresh), not a fresh read. null: a fresh read.
  const [offlineAsOf, setOfflineAsOf] = useState<string | null>(null)
  // deck_busy (spec amendment 2): a saved-deck write lost the 3s lock race with
  // a Watchtower publish and is waiting to retry. The EDIT IS NOT DISCARDED and
  // no error is shown - the first busy is a wait, not a failure. Counted rather
  // than boolean so two writes in flight cannot have the first one's retry turn
  // the indicator off under the second.
  const [savingBusy, setSavingBusy] = useState(false)
  const busyWrites = useRef(0)
  const beginBusy = useCallback(() => {
    busyWrites.current += 1
    setSavingBusy(true)
  }, [])
  const endBusy = useCallback(() => {
    busyWrites.current = Math.max(0, busyWrites.current - 1)
    if (busyWrites.current === 0) setSavingBusy(false)
  }, [])

  /**
   * Run one saved_decks write through the deck_busy contract.
   *
   * Non-busy outcomes are untouched: the caller gets the same `{ data, error }`
   * it has always got, and every existing rollback / error path behaves exactly
   * as before. A busy first attempt is absorbed - quiet indicator, one retry
   * after 5s - and whatever the retry produces is what the caller sees, so an
   * error is surfaced once and only if the retry also failed.
   */
  const runDeckWrite = useCallback(async <T extends { error?: unknown }>(
    attempt: () => PromiseLike<T>,
  ): Promise<T> => {
    const outcome = await withDeckBusyRetry(attempt, {
      isBusyValue: v => deckBusy(v?.error),
      onBusy: beginBusy,
      delayMs: DECK_BUSY_RETRY_MS,
    })
    if (outcome.retried) endBusy()
    if (outcome.ok) return outcome.value
    // Only reachable after a retry we would not have made unless the first
    // failure was a lock loss. Fold the rejection into the envelope the caller
    // already reads so it surfaces as one ordinary error rather than an
    // unhandled rejection.
    return { data: null, error: rejectionAsError(outcome.error) } as unknown as T
  }, [beginBusy, endBusy])

  // Load cloud decks when user logs in. Local decks stay invisible in the
  // authenticated list until the user accepts the migration prompt below;
  // showing them automatically used to leak the previous user's decks across
  // accounts on a shared device. The import flow is the single gate.
  useEffect(() => {
    // Wait for Supabase auth to settle before deciding cloud vs local.
    // Pre-fix this branch ran with user=null during auth restore, briefly
    // displaying local decks to signed-in users.
    if (authLoading) return
    // One load, restartable: a guarded write that lands in THIS tab while a
    // load is in flight restarts it (see the persisted-row listener), so the
    // list never settles on a read taken before that write.
    const startLoad = (background = false) => {
    const token = ++loadSeqRef.current
    const epoch = mutationEpochRef.current
    // Drafts written from here on are never pruned by this load.
    const startedAt = Date.now()
    loadInFlightRef.current = true
    // On completion: may this load install its rows? A newer load owns the
    // list (token), and a load that a mutation overtook re-reads instead,
    // once, in the background.
    const settle = (): boolean => {
      if (token !== loadSeqRef.current) return false
      loadInFlightRef.current = false
      if (mutationEpochRef.current !== epoch) {
        startLoad(true)
        return false
      }
      return true
    }
    // A load always reads AFTER every guarded write already in flight for
    // this user (the editor's instant saves, re-rates): it waits for them
    // (capped at 10s; an offline write retrying longer is covered by its
    // draft and its stale handling). Nothing in flight: read at once.
    // eslint-disable-next-line i18next/no-literal-string -- a scope code
    const pending = pendingGuardedWrites(user?.id ?? 'guest')

    if (!isCloud || !supabase) {
      const finishLocal = () => {
        if (!settle()) return
        const fresh = readLocalDecksStrict()
        installLoadedDecks(fresh ?? [])
        // A complete, readable list: the editor drafts of decks it no longer
        // holds belong to deleted decks.
        // eslint-disable-next-line i18next/no-literal-string -- a scope code
        if (fresh) pruneDraftsForMissingDecks('guest', fresh.map(d => d.id), startedAt)
        setLoading(false)
      }
      // Under the same Web Lock as guest writes, so a read never lands in
      // the middle of another tab's read / compare / write.
      const locks = typeof navigator !== 'undefined'
        ? (navigator as Navigator & { locks?: { request: (name: string, cb: () => Promise<void>) => Promise<void> } }).locks
        : undefined
      // eslint-disable-next-line i18next/no-literal-string -- a Web Lock name
      const readLocal = () => (locks?.request ? void locks.request('cb-saved-decks', async () => finishLocal()) : finishLocal())
      if (pending) void pending.then(readLocal)
      else readLocal()
      return
    }

    if (!background) setLoading(true)
    const sb = supabase
    // Offline contracts C2 / W3. An account whose session is not usable
    // (offline past expiry, a refresh pending, "Sign in again") never reads
    // the table: an anonymous read answers an EMPTY list through RLS, which
    // would read as every deck deleted (and prune their drafts). It shows
    // the decks stored on this device instead, as does a read that fails.
    const offlineOwnerId = user && offlineRuntimeActive() ? user.id : null
    const sessionUsable = () => !offlineOwnerId || getIdentity().mode === MODE.active
    const showStored = (fallback: () => void) => {
      if (!offlineOwnerId) {
        fallback()
        setLoading(false)
        return
      }
      void readOwnerDecks(offlineOwnerId).then((snap) => {
        if (token !== loadSeqRef.current) return
        if (snap) {
          installLoadedDecks(snap.decks)
          setOfflineAsOf(snap.refreshedAt ?? '')
          setCloudSyncError(null)
        } else {
          fallback()
        }
        setLoading(false)
      }, () => {
        if (token !== loadSeqRef.current) return
        fallback()
        setLoading(false)
      })
    }
    if (!sessionUsable()) {
      loadInFlightRef.current = false
      showStored(() => installLoadedDecks([]))
      return
    }
    // The LIVE session must be the owner's, checked before the read is sent
    // and again before anything is installed, stored or pruned (finding 15):
    // auth-js publishes an incoming account's session to shared storage
    // before C3 runs, and a read under it answers an EMPTY list through RLS.
    const sessionOwned = async (): Promise<boolean> => {
      if (!offlineOwnerId) return true
      try {
        const { data } = await sb.auth.getSession()
        return data.session?.user?.id === offlineOwnerId
      } catch {
        return false
      }
    }
    const runLoad = async () => {
      if (!(await sessionOwned())) {
        if (!settle()) return
        showStored(() => installLoadedDecks([]))
        return
      }
      return runQuery()
    }
    const runQuery = () => sb
      .from('saved_decks')
      .select('*')
      // Explicit owner filter (defense-in-depth): the public-read RLS policy
      // grants SELECT on is_public rows, so an unfiltered select could surface
      // other users' public decks here. RLS already scopes to the user, so for
      // a normal account this returns the identical set.
      .eq('user_id', user?.id ?? '')
      // Most recently TOUCHED first (updated_at moves on every save), so a
      // deck the user just rebuilt surfaces at the top instead of sitting at
      // its original creation slot. nullsFirst: false keeps legacy rows with
      // a NULL updated_at at the bottom rather than pinned above everything.
      .order('updated_at', { ascending: false, nullsFirst: false })
      .then(async ({ data, error }) => {
        const owned = error ? true : await sessionOwned()
        // A newer load started since, or a mutation overtook this one: its
        // rows are stale and are not installed.
        if (!settle()) return
        if (error || !sessionUsable() || !owned) {
          if (error) console.error('Failed to load cloud decks:', error)
          // The stored copy when there is one (W3); otherwise as before:
          // cloud-mode shows cloud decks only; surface error via cloudSyncError.
          showStored(() => {
            installLoadedDecks([])
            setCloudSyncError(tc('saved_decks.load_failed'))
          })
          return
        } else {
          const rows = (data as DbRow[]).map(mapRowToDeck)
          installLoadedDecks(rows)
          // Another device, another tab or the deck-box runner may have
          // changed My Decks: a list that differs from the last one seen
          // drops the deck-dependent collection data.
          if (user) noteSavedDecksLoaded(user.id, rows)
          setOfflineAsOf(null)
          // The complete list is what this device keeps for offline use.
          if (offlineOwnerId) void storeDeckSnapshot(offlineOwnerId, rows, { complete: true })
          // A complete owner-scoped list: drafts of decks it no longer holds
          // belong to decks deleted elsewhere.
          if (user) pruneDraftsForMissingDecks(user.id, (data as DbRow[]).map(r => r.id), startedAt)
          // Once we've loaded cloud data AND the user has already decided
          // about local-deck migration (server flag in user_metadata OR
          // localStorage flag set), wipe the stale local mirror so it
          // can't resurface on sign-out or re-trigger the migration
          // prompt. New unmigrated users (no flag set) are NOT touched —
          // the migration prompt path below still has access to their
          // localStorage to offer the import.
          const dismissedServer = !!(user?.user_metadata as Record<string, unknown> | undefined)?.[
            'saved_decks_migration_dismissed'
          ]
          const dismissedLocal = !!localStorage.getItem(MIGRATION_KEY)
          if (dismissedServer || dismissedLocal) {
            try {
              localStorage.removeItem(STORAGE_KEY)
            } catch {
              // ignore — best-effort cleanup
            }
          }
        }
        setLoading(false)
      })
    if (pending) void pending.then(runLoad)
    else void runLoad()
    }
    reloadRef.current = startLoad
    startLoad()
    if (!isCloud || !supabase) return

    // Check if there are local decks to migrate. The prompt is the only
    // pathway by which local decks join the authenticated list.
    //
    // Suppression is checked on BOTH user_metadata (server-side, survives
    // sign-out) AND localStorage (client-side, survives page reload while
    // signed in). Pre-2026-05-09 the flag lived only in localStorage,
    // which gets cleared on sign-out - so users who skipped the prompt
    // would see it again every time they signed back in. The server-side
    // flag fixes that loop.
    const localDecks = loadLocalDecks()
    if (localDecks.length > 0) {
      const localFlag = localStorage.getItem(MIGRATION_KEY)
      const serverFlag = (user?.user_metadata as Record<string, unknown> | undefined)?.[
        'saved_decks_migration_dismissed'
      ]
      if (!localFlag && !serverFlag) {
        setMigrationNeeded(true)
      }
    }
  }, [authLoading, isCloud, user, setDecks, installLoadedDecks])

  // The COMMITTED writer and the mode it was built for, updated in a layout
  // effect (never during render). One save reads it once, before any lock,
  // so the lock decision and the writer always come from the same commit.
  type WriterImpl = (
    analysis: CompleteAnalysis, decklistText: string | undefined, partnerName: string | null | undefined,
    opts: SaveDeckOptions | undefined, checked: boolean,
  ) => Promise<{ result: SaveDeckResult; outcome: SaveOutcome }>
  const committedWriterRef = useRef<{ impl: WriterImpl; isCloud: boolean; scope: string } | null>(null)

  // The ONE content writer behind saveDeck and saveDeckChecked. It returns
  // both shapes; each public function reads its own. `checked` changes only
  // the retry rule (a fatal cloud error is not retried) and two guest
  // refusals saveDeck never made (see below).
  const writeDeck = useCallback(async (
    analysisIn: CompleteAnalysis,
    decklistText: string | undefined,
    partnerName: string | null | undefined,
    opts: SaveDeckOptions | undefined,
    checked: boolean,
  ): Promise<{ result: SaveDeckResult; outcome: SaveOutcome }> => {
    // Guest mode: localStorage is the store of record and other tabs write it
    // too. The whole read / compare / write runs under one Web Lock where the
    // browser has them, so two tabs cannot both pass the compare-and-swap on
    // the same stored text. Without navigator.locks (older browsers, jsdom)
    // it runs as before: synchronously up to the write, which narrows but
    // does not close the cross-tab window.
    const snap = committedWriterRef.current
    // eslint-disable-next-line i18next/no-literal-string -- developer log only
    if (!snap) throw new Error('useSavedDecks: save before the first commit')
    if (opts?.expectedScope !== undefined && opts.expectedScope !== snap.scope) {
      return {
        // eslint-disable-next-line i18next/no-literal-string -- a refusal code
        result: { id: '', cloudSynced: false, isCloud: snap.isCloud, persistedLocally: false, refused: 'stale' },
        // eslint-disable-next-line i18next/no-literal-string -- an outcome code
        outcome: { status: 'stale' },
      }
    }
    const locks = !snap.isCloud && typeof navigator !== 'undefined'
      ? (navigator as Navigator & { locks?: { request: <T>(name: string, cb: () => Promise<T>) => Promise<T> } }).locks
      : undefined
    const run = () => snap.impl(analysisIn, decklistText, partnerName, opts, checked)
    const raw: Promise<{ result: SaveDeckResult; outcome: SaveOutcome }> = locks?.request
      // eslint-disable-next-line i18next/no-literal-string -- a Web Lock name
      ? locks.request('cb-saved-decks', run) as unknown as Promise<{ result: SaveDeckResult; outcome: SaveOutcome }>
      : run()
    // A cloud write that landed (or may have: an unknown outcome) changed My
    // Decks: the collection data that depends on them (spare smart binders)
    // is dropped. Every content save goes through here: a typed save, a
    // version restore, a source refresh, a re-rate.
    const write = raw.then((r) => {
      if (!snap.isCloud) return r
      const status = r.outcome.status
      // eslint-disable-next-line i18next/no-literal-string -- outcome codes
      if (status === 'saved' || status === 'unknown') decksChangedRef.current(snap.scope)
      // A refusal reconciled the list with the server: a stale one (an
      // ordinary checked save, or a lost write resolved as stale) installed
      // the stored row; a missing one dropped it. Either is a change when the
      // row differs from the last trusted stamp of that deck.
      // eslint-disable-next-line i18next/no-literal-string -- outcome codes
      else if ((status === 'stale' || status === 'missing') && r.result.id) {
        // eslint-disable-next-line i18next/no-literal-string -- an outcome code
        const row = status === 'missing' ? null : decksRef.current.find(d => d.id === r.result.id) ?? null
        noteServerDeckRow(snap.scope, r.result.id, row)
      }
      return r
    })
    // A guarded write is waited for by every load of its scope (see the load
    // effect), so no list can read the row before it lands.
    // Resolving a lost write counts: it can still write (its identical retry).
    const guarded = (!!opts?.existingId && !!opts.updateOnly && opts.expectedDecklist !== undefined) || !!opts?.resolveUnknown
    return guarded ? trackGuardedWrite(snap.scope, write) : write
  }, [])

  // ---- Rev-guarded cloud writes ---------------------------------------------
  //
  // The protocol (also in the API migration that added saved_decks.rev):
  //  - a save PATCHes `id = X AND rev = N` (N: the rev its edit is based on)
  //    with Prefer: return=representation, and adopts the returned rev;
  //  - zero rows: re-read; the row already holding this write is saved; one
  //    whose list is still the edit's base is retried ONCE against its rev
  //    (card_meta re-merged per field, a same-field conflict is stale); any
  //    other row is stale;
  //  - a lost response (isAmbiguousFailure) is UNKNOWN: re-read; rev still N
  //    -> the IDENTICAL request again (both compete for one revision, at most
  //    one commits); the row holding it -> saved; anything else -> stale.
  //    Never retried against a newer rev.

  type DeckRead =
    | { ok: true; row: Partial<DbRow> | null }
    | { ok: false; error: { message?: string; code?: string } | null; status: number | undefined; cause: unknown }
  // The owner's row, whole (the rev and every column a save compares).
  const readDeckRow = async (sb: NonNullable<typeof supabase>, userId: string, id: string): Promise<DeckRead> => {
    try {
      const { data, error, status } = await sb.from('saved_decks').select('*').eq('id', id).eq('user_id', userId).limit(1)
      if (error) return { ok: false, error, status, cause: error }
      return { ok: true, row: (data as Partial<DbRow>[] | null)?.[0] ?? null }
    } catch (e) {
      return { ok: false, error: null, status: undefined, cause: e }
    }
  }
  type GuardedUpdate = { rows: Partial<DbRow>[] | null; error: { message?: string; code?: string } | null; status: number | undefined; cause: unknown }
  // ONE guarded UPDATE: `rev = N` (when known) and the decklist guard (when
  // given), returning the rows it changed. A throw is a lost response.
  const guardedUpdate = async (
    sb: NonNullable<typeof supabase>, userId: string,
    a: { deckId: string; rev?: number; expectedDecklist?: string | null; payload: Record<string, unknown> },
  ): Promise<GuardedUpdate> => {
    try {
      const { data, error, status } = await runDeckWrite(() => {
        /* eslint-disable i18next/no-literal-string -- Supabase table and columns */
        let q = sb.from('saved_decks').update(a.payload).eq('id', a.deckId).eq('user_id', userId)
        if (a.rev !== undefined) q = q.eq('rev', a.rev)
        if (a.expectedDecklist !== undefined) q = a.expectedDecklist === null ? q.is('decklist', null) : q.eq('decklist', a.expectedDecklist)
        return q.select('*')
        /* eslint-enable i18next/no-literal-string */
      })
      return { rows: (data as Partial<DbRow>[] | null) ?? null, error: error ?? null, status, cause: error }
    } catch (e) {
      // eslint-disable-next-line i18next/no-literal-string -- developer log only (console), never shown
      return { rows: null, error: { message: e instanceof Error ? e.message : 'Cloud write threw' }, status: undefined, cause: e }
    }
  }
  type AttemptResolution =
    | { kind: 'saved'; row: Partial<DbRow> }
    | { kind: 'stale'; row: Partial<DbRow> }
    | { kind: 'missing' }
    | { kind: 'unknown'; cause: unknown; fatalCode?: SaveFatalCode }
  /**
   * Settle a lost write. The attempt is never changed: its retry is the SAME
   * payload with the SAME guards, sent only while the row is still at its
   * rev, and at most once per call.
   */
  const resolveAttempt = async (sb: NonNullable<typeof supabase>, userId: string, a: UnknownSaveAttempt): Promise<AttemptResolution> => {
    let retried = false
    for (;;) {
      const read = await readDeckRow(sb, userId, a.deckId)
      if (!read.ok) return { kind: 'unknown', cause: read.cause }
      // eslint-disable-next-line i18next/no-literal-string -- resolution codes
      if (!read.row) return { kind: 'missing' }
      const atN = revOf(read.row) === a.rev
      // A must-consume attempt held at N itself did NOT consume N: retry.
      if (rowHoldsPayload(read.row, a.payload) && !(a.mustConsumeRev && atN)) return { kind: 'saved', row: read.row }
      if (!atN || retried) return { kind: 'stale', row: read.row }
      // Still at N: it did not commit (yet). The identical request competes
      // with it for revision N.
      retried = true
      const res = await guardedUpdate(sb, userId, a)
      if (res.error) {
        const kind = classifyCloudError(res.error, res.status)
        // Rejected (a 401, ...): the ORIGINAL may still have committed before
        // it, so the attempt stays unknown; only retrying stops.
        if (!isAmbiguousFailure(res.error, res.status) && kind.kind === 'fatal') return { kind: 'unknown', cause: res.cause, fatalCode: kind.code }
        return { kind: 'unknown', cause: res.cause }
      }
      const hit = res.rows?.[0]
      if (hit) return { kind: 'saved', row: hit }
      // Zero rows: something moved the row; read it once more.
    }
  }

  // saveDeckChecked(..., { resolveUnknown }): settle a lost write, nothing else.
  const resolveUnknownImpl = async (a: UnknownSaveAttempt): Promise<{ result: SaveDeckResult; outcome: SaveOutcome }> => {
    const base: SaveDeckResult = { id: a.deckId, cloudSynced: false, isCloud, persistedLocally: false }
    // Signed out: the attempt stays unknown (nothing settled it), stopped.
    // eslint-disable-next-line i18next/no-literal-string -- outcome codes
    const fatalAuth = (): { result: SaveDeckResult; outcome: SaveOutcome } => ({ result: base, outcome: { status: 'unknown', attempt: a, cause: new Error('not signed in'), fatalCode: 'auth' } })
    if (!isCloud || !supabase || !user) return fatalAuth()
    const sb = supabase
    try {
      const { data: { session } } = await sb.auth.getSession()
      if (!session) return fatalAuth()
    } catch (cause) {
      // eslint-disable-next-line i18next/no-literal-string -- an outcome code
      return { result: base, outcome: { status: 'unknown', attempt: a, cause } }
    }
    const res = await resolveAttempt(sb, user.id, a)
    const local = decksRef.current.find(d => d.id === a.deckId)
    const asDeck = (row: Partial<DbRow>): SavedDeck => {
      const fallback: SavedDeck = local ?? mapRowToDeck({ ...(row as DbRow), created_at: row.created_at ?? new Date(0).toISOString() })
      const deck = deckFromDbRow(row, fallback)
      // A full save's analysis is the current version's.
      if ('analysis' in a.payload) deck.analysisVersion = CURRENT_ANALYSIS_VERSION
      return deck
    }
    switch (res.kind) {
      case 'saved': {
        const deck = asDeck(res.row)
        bumpEpoch()
        setDecks(prev => prev.map(d => (d.id === deck.id ? deck : d)))
        // eslint-disable-next-line i18next/no-literal-string -- a scope code
        broadcastPersisted(user.id ?? 'guest', deck, instanceIdRef.current)
        // eslint-disable-next-line i18next/no-literal-string -- an outcome code
        return { result: { ...base, cloudSynced: true }, outcome: { status: 'saved', deck } }
      }
      case 'stale': {
        // This list shows what the database holds.
        const deck = asDeck(res.row)
        bumpEpoch()
        setDecks(prev => prev.map(d => (d.id === deck.id ? deck : d)))
        // eslint-disable-next-line i18next/no-literal-string -- a refusal code
        return { result: { ...base, refused: 'stale' }, outcome: { status: 'stale' } }
      }
      case 'missing':
        // eslint-disable-next-line i18next/no-literal-string -- a refusal code
        return { result: { ...base, refused: 'missing' }, outcome: { status: 'missing' } }
      case 'unknown':
        // eslint-disable-next-line i18next/no-literal-string -- an outcome code
        return { result: base, outcome: { status: 'unknown', attempt: a, cause: res.cause, ...(res.fatalCode ? { fatalCode: res.fatalCode } : {}) } }
    }
  }

  // Hold a pulled list the account already has a row for (an unguarded link
  // save that found one, see linkedImportConflict) as the deck's refresh
  // choice. The later "Use the {source} version" write is a refresh: it
  // never renames the deck and never moves its bucket.
  const holdLinkedChoice = (deckId: string, link: NonNullable<SaveDeckOptions['link']>, pending: LinkedPending | null) => {
    if (!pending) return
    setRefreshChoice({
      // eslint-disable-next-line i18next/no-literal-string -- a scope code
      scope: user?.id ?? 'guest',
      deckId,
      // eslint-disable-next-line i18next/no-literal-string -- a choice kind
      kind: 'stale',
      write: {
        analysis: pending.analysis,
        decklist: pending.decklist,
        partner: pending.partner,
        // eslint-disable-next-line i18next/no-literal-string -- a link mode
        link: { sourceUrl: link.sourceUrl, source: link.source, decklistHash: link.decklistHash, mode: 'refresh' },
      },
      source: link.source,
    })
  }

  /**
   * An unguarded link save's target, when the account already has it: the
   * row for the URL (or the existingId row). null = none (insert). A row that
   * holds exactly this write is this save, already done; any other is NOT
   * written: refused as stale and held for the user's choice.
   */
  const linkedImportConflict = async (a: {
    link: NonNullable<SaveDeckOptions['link']>
    existingId?: string
    freshLocal: SavedDeck[] | null
    usedDecklist: string | undefined
    payload: (userId: string) => Record<string, unknown>
    pending: LinkedPending | null
  }): Promise<{ result: SaveDeckResult; outcome: SaveOutcome } | null> => {
    const stale = (id: string) => ({
      // eslint-disable-next-line i18next/no-literal-string -- a refusal code
      result: { id, cloudSynced: false, isCloud, persistedLocally: false, refused: 'stale' } as SaveDeckResult,
      // eslint-disable-next-line i18next/no-literal-string -- a refusal code
      outcome: { status: 'stale' } as SaveOutcome,
    })
    if (!isCloud) {
      const list = a.freshLocal
      if (!list) return null
      const row = list.find(d => d.sourceUrl === a.link.sourceUrl) ?? (a.existingId ? list.find(d => d.id === a.existingId) : undefined)
      if (!row) return null
      // eslint-disable-next-line i18next/no-literal-string -- a scope code
      if (rowHoldsPayload(deckAsRow(row, 'guest'), a.payload('guest'))) {
        // eslint-disable-next-line i18next/no-literal-string -- an outcome code
        return { result: { id: row.id, cloudSynced: false, isCloud, persistedLocally: true }, outcome: { status: 'saved', deck: row } }
      }
      bumpEpoch()
      setDecks(list)
      holdLinkedChoice(row.id, a.link, a.pending)
      return stale(row.id)
    }
    // Signed out: the normal path reports it.
    if (!supabase || !user) return null
    const sb = supabase
    const failure = (error: { message?: string; code?: string } | null, status: number | undefined, cause: unknown) => {
      const kind = classifyCloudError(error, status)
      return {
        result: { id: a.existingId ?? '', cloudSynced: false, isCloud, persistedLocally: false } as SaveDeckResult,
        // eslint-disable-next-line i18next/no-literal-string -- outcome codes
        outcome: (kind.kind === 'fatal' ? { status: 'fatal_error', code: kind.code, cause } : { status: 'retryable_error', cause }) as SaveOutcome,
      }
    }
    let row: Partial<DbRow> | undefined
    try {
      const { data, error, status } = await sb.from('saved_decks').select('*').eq('user_id', user.id).eq('source_url', a.link.sourceUrl).limit(1)
      if (error) return failure(error, status, error)
      row = (data as Partial<DbRow>[] | null)?.[0]
    } catch (cause) {
      return failure(null, undefined, cause)
    }
    if (!row && a.existingId) {
      const read = await readDeckRow(sb, user.id, a.existingId)
      if (!read.ok) return failure(read.error, read.status, read.cause)
      row = read.row ?? undefined
    }
    if (!row || !row.id) return null
    const local = decksRef.current.find(d => d.id === row!.id)
    const deck = local ? deckFromDbRow(row, local) : mapRowToDeck({ ...(row as DbRow), created_at: row.created_at ?? new Date(0).toISOString() })
    if (rowHoldsPayload(row, a.payload(user.id))) {
      // eslint-disable-next-line i18next/no-literal-string -- an outcome code
      return { result: { id: deck.id, cloudSynced: true, isCloud, persistedLocally: false }, outcome: { status: 'saved', deck } }
    }
    // This list shows the row as stored; nothing is written.
    bumpEpoch()
    setDecks(prev => (prev.some(d => d.id === deck.id) ? prev.map(d => (d.id === deck.id ? deck : d)) : [deck, ...prev]))
    holdLinkedChoice(deck.id, a.link, a.pending)
    return stale(deck.id)
  }

  const writeDeckUnlockedImpl = async (
    analysisIn: CompleteAnalysis,
    decklistText: string | undefined,
    partnerName: string | null | undefined,
    opts: SaveDeckOptions | undefined,
    checked: boolean,
  ): Promise<{ result: SaveDeckResult; outcome: SaveOutcome }> => {
    if (opts?.resolveUnknown) return resolveUnknownImpl(opts.resolveUnknown)
    // Guest mode reads the list FRESH from storage and builds everything from
    // it (prior rows, the list it writes back), so another tab's decks and
    // metadata are never written over with this tab's stale copy. Cloud mode
    // uses the CURRENT in-memory list, not this callback's closure: a caller
    // can hold an old saveDeck (DeckDetailV2's recompute keeps running after
    // its screen unmounts).
    const freshLocal = isCloud ? null : readLocalDecksStrict()
    if (!isCloud && freshLocal === null && checked) {
      // eslint-disable-next-line i18next/no-literal-string -- developer log only (console), never shown
      const cause = new Error('localStorage is not readable')
      // eslint-disable-next-line i18next/no-literal-string -- a failure code
      return { result: { id: opts?.existingId ?? '', cloudSynced: false, isCloud, persistedLocally: false }, outcome: { status: 'fatal_error', code: 'storage', cause } }
    }
    const current = freshLocal ?? decksRef.current
    const usedDecklist = decklistText ?? decklist
    const refuse = (refused: 'missing' | 'stale') => ({
      result: { id: opts?.existingId ?? '', cloudSynced: false, isCloud, persistedLocally: false, refused } as SaveDeckResult,
      outcome: { status: refused } as SaveOutcome,
    })
    // Replace one row in the list with the row as stored.
    const install = (deck: SavedDeck) => setDecks(prev => prev.map(d => (d.id === deck.id ? deck : d)))

    const keep = !!opts?.keepAnalysis
    // The no-op fence (keepAnalysis + fenceOnly + mustConsumeRev).
    const fence = keep && !!opts?.fenceOnly
    // A zone-carrying instant save only counts as "already landed" on a row
    // whose commander / partner columns ALSO hold that zone (front-face
    // compare): the same text written by a writer that left the columns
    // alone is not this write, and is refused as stale instead.
    const zoneLanded = (r: { commander?: string | null; partner?: string | null }): boolean => {
      const z = keep ? opts?.commandZone : undefined
      if (!z) return true
      const k = (n: string | null | undefined) => (n ? frontKey(n.trim()) : '')
      return k(r.commander) === k(z.commander) && k(r.partner) === k(z.partner)
    }
    // An instant save carrying the editor's card_meta (Wave 4): stored null
    // when empty. It only counts as "already landed" on a row that ALSO
    // holds that card_meta, like the zone above.
    let metaWrite: CardMetaMap | null | undefined = keep && opts?.cardMeta !== undefined ? storedCardMeta(opts.cardMeta) : undefined
    // Cloud merge only: the WHOLE row the merge was read from. Its rev guards
    // the update, so a card_meta (or anything else) written between the read
    // and the write is never replaced: that miss re-reads and re-merges once
    // (onto the new rev), then is stale. This replaced a jsonb equality
    // filter on card_meta carried in the URL.
    let mergeRead: Partial<DbRow> | undefined
    // false: the stored value moved a field the caller's delta moves too (a
    // conflict); nothing is merged and the save is refused as stale.
    const remergeOnto = (raw: unknown): boolean => {
      const merged = mergeCardMetaDelta(parseCardMeta(raw ?? null), opts!.cardMetaMerge!.base, opts!.cardMetaMerge!.next)
      if (merged.conflicts.length > 0) return false
      metaWrite = storedCardMeta(opts!.cardMetaNoPrune ? merged.meta : pruneCardMetaForText(merged.meta, usedDecklist))
      return true
    }
    const metaLanded = (stored: unknown): boolean =>
      metaWrite === undefined || canonicalCardMeta(parseCardMeta(stored)) === canonicalCardMeta(metaWrite)
    const priorForKeep = keep && opts?.existingId ? current.find(d => d.id === opts.existingId) : undefined
    // eslint-disable-next-line i18next/no-literal-string -- a refusal code
    if (keep && !priorForKeep) return refuse('missing')
    if (keep && priorForKeep && opts?.cardMetaMerge && metaWrite === undefined) {
      // The caller's view of card_meta is not the stored one: read it fresh
      // (the row itself, guest or cloud) and apply only the caller's changes.
      let fresh: unknown = priorForKeep.cardMeta
      if (isCloud && supabase && user) {
        const { data, error, status } = await supabase.from('saved_decks').select('*').eq('id', priorForKeep.id).eq('user_id', user.id).limit(1)
        if (error) {
          const kind = classifyCloudError(error, status)
          return {
            result: { id: opts.existingId ?? '', cloudSynced: false, isCloud, persistedLocally: false },
            // eslint-disable-next-line i18next/no-literal-string -- outcome codes
            outcome: kind.kind === 'fatal' ? { status: 'fatal_error', code: kind.code, cause: error } : { status: 'retryable_error', cause: error },
          }
        }
        const r = (data as Partial<DbRow>[] | null)?.[0]
        // eslint-disable-next-line i18next/no-literal-string -- a refusal code
        if (!r) return refuse('missing')
        fresh = r.card_meta
        mergeRead = r
      }
      // eslint-disable-next-line i18next/no-literal-string -- a refusal code
      if (!remergeOnto(fresh)) return refuse('stale')
    }
    if (metaWrite && cardMetaTooLarge(metaWrite)) {
      // eslint-disable-next-line i18next/no-literal-string -- developer log only (console), never shown
      const cause = new Error('card_meta is over the size limit')
      return {
        result: { id: opts?.existingId ?? '', cloudSynced: false, isCloud, persistedLocally: false },
        // eslint-disable-next-line i18next/no-literal-string -- a failure code
        outcome: { status: 'fatal_error', code: 'invalid', cause },
      }
    }

    if (opts?.updateOnly || opts?.expectedDecklist !== undefined) {
      const row = opts.existingId ? current.find(d => d.id === opts.existingId) : undefined
      // eslint-disable-next-line i18next/no-literal-string -- a refusal code
      if (!row) return refuse('missing')
      // A cloud link save based on an explicit rev (a refresh's captured
      // base, or the fresh read "Use the {source} version" just took) is
      // decided by the database's compare-and-swap alone: this list can lag
      // a row another device wrote, and refusing on it would ask the same
      // question forever without the write ever reaching the table. The
      // captured rev still refuses a writer that came after that read.
      const dbDecides = isCloud && !!opts.link && !keep && typeof opts.expectedRev === 'number'
      if (!dbDecides && opts.expectedDecklist !== undefined && (row.decklist ?? null) !== opts.expectedDecklist) {
        // Guest: the stored row moved to exactly the text this write carries
        // (another tab made the same edit): this write is already done.
        if (!isCloud && freshLocal && (row.decklist ?? null) === (usedDecklist ?? null) && zoneLanded(row) && metaLanded(row.cardMeta)) {
          setDecks(freshLocal)
          if (opts.updateOnly) {
            bumpEpoch()
            // eslint-disable-next-line i18next/no-literal-string -- a scope code
            broadcastPersisted(user?.id ?? 'guest', row, instanceIdRef.current)
          }
          return {
            result: { id: row.id, cloudSynced: false, isCloud, persistedLocally: true },
            // eslint-disable-next-line i18next/no-literal-string -- an outcome code
            outcome: { status: 'saved', deck: row },
          }
        }
        // Cloud: this list already shows the target text (a landed write of
        // another instance was broadcast here); the database's compare-and-
        // swap decides, and converges on the stored row.
        if (isCloud ? (row.decklist ?? null) !== (usedDecklist ?? null) : true) {
          // Guest: show what storage holds (the refusal's truth).
          if (!isCloud && freshLocal) {
            bumpEpoch()
            setDecks(freshLocal)
          }
          // eslint-disable-next-line i18next/no-literal-string -- a refusal code
          return refuse('stale')
        }
      }
    }

    // Full saves stamp the hash of the list they were analyzed from, so a
    // later instant save can tell the rating is behind. A keepAnalysis save
    // never touches the analysis at all: a legacy row stays unstamped until
    // its next full save, and another tab's newer rating is never written
    // back over.
    const analysis: CompleteAnalysis = keep
      ? priorForKeep!.analysis
      : usedDecklist !== undefined
        ? { ...analysisIn, analyzed_content_hash: analysisContentHash(usedDecklist) }
        : analysisIn
    // The analysis-derived fields (lib/savedDeckFields, shared with the
    // deck-box runner's commit). An instant save keeps the row's own.
    const derived = keep ? null : savedDeckFields(analysis)
    // An instant save that moved the command zone writes it with the list.
    const zoneWrite = keep ? opts?.commandZone : undefined
    const commander = keep ? (zoneWrite ? zoneWrite.commander : priorForKeep!.commander) : derived!.commander
    // Explicit !== false, not a truthiness check: every existing caller omits
    // the flag entirely and must keep bumping exactly as it does today. An
    // instant save never moves the deck in My Decks.
    const bumpRecency = !keep && opts?.bumpRecency !== false
    // Only used for the non-link commander+partner dedup match below (which a
    // link save never reaches - see priorByCommander's own guard). The FINAL
    // partner used for the row itself is computed after `prior` is resolved,
    // a few lines down, so a link-refresh can override it with the prior
    // row's partner instead.
    const requestedPartner = partnerName ?? null
    const engine = keep ? priorForKeep!.engine : derived!.engine
    const bracket = keep ? priorForKeep!.bracket : derived!.bracket
    // An instant save counts the list itself with the SAME rule the analysis
    // counts by (main + commander + companion; never sideboard, maybeboard
    // or tokens), so the tile's count does not jump when the rating lands.
    const cardCount = keep ? countDecklistCards(usedDecklist ?? '') : derived!.cardCount
    // A link payload never rides an instant save.
    const link = keep ? undefined : opts?.link

    // Resolve the target row id + prior row up front (closure has current decks)
    // so the returned id matches what we store. An explicit existingId (inline
    // edit of a specific row) wins over the legacy commander+partner dedup,
    // which is what fixes editing a deck that shares a commander with another.
    const priorById = opts?.existingId ? current.find(d => d.id === opts.existingId) : undefined
    // Linked decks dedup on the SOURCE URL, never on commander+partner. Three
    // Atraxa brews imported from Archidekt are three decks; collapsing them on
    // commander would silently destroy two of them.
    let priorForLink: SavedDeck | undefined
    // A GUARDED link save (a refresh / link pull of a known row, rev
    // compare-and-swap) targets exactly its existingId row, never a sourceUrl
    // match: the base it was captured with describes that row only.
    const guardedLink = !!link && !!opts?.existingId && !!opts.updateOnly && opts.expectedDecklist !== undefined
    if (link && !guardedLink) {
      priorForLink = current.find(d => d.sourceUrl === link.sourceUrl)
    }
    // Null-aware: an unknown-commander analysis must still dedupe against its
    // own prior row, or repeated saves (e.g. share auto-save) spawn duplicates.
    // For null-commander decks the match key is the DECKLIST CONTENT - two
    // different unknown-commander decks must never merge into one row.
    // Skipped entirely for linked saves - a link never falls through to
    // commander+partner matching, which could silently overwrite an unrelated
    // deck that happens to share a commander.
    const priorByCommander = (opts?.forceNew || link || keep) ? undefined : current.find(d =>
      (d.commander ?? null) === commander &&
      (d.partner ?? null) === requestedPartner &&
      (commander !== null || (d.decklist ?? '') === (usedDecklist ?? ''))
    )
    // A forceNew copy must not inherit ANY prior row's state - not its id, and
    // not its share token / Library listing (two rows sharing a share token
    // would collide). A link save with no sourceUrl match falls back to an
    // explicit existingId (attaching a link to an already-known row) before
    // it is treated as a brand-new deck - this MUST mirror the cloud
    // resolution order in attemptCloudSync below exactly (sourceUrl match,
    // then existingId, then new row), or local state and the cloud write can
    // target different rows when a caller passes both.
    const prior = opts?.forceNew
      ? undefined
      : link
        ? (priorForLink ?? priorById)
        : (priorById ?? priorByCommander)
    const targetId = link
      ? (priorForLink?.id ?? opts?.existingId ?? generateId())
      : (opts?.existingId ?? prior?.id ?? generateId())
    const attempt = ++contentAttemptSeq.current
    contentAttemptRef.current.set(targetId, attempt)

    // A REFRESH used to carry the prior row's partner forward unconditionally,
    // because the caller passed a hardcoded `null` and taking it would silently
    // erase a partner deck's second commander on every re-pull.
    //
    // That protected the wrong thing. The source can legitimately DROP, GAIN or
    // SWAP a partner, and carrying the old one forward leaves the row holding a
    // single-commander list while still displaying, sharing and exporting a
    // commander that is no longer in the deck.
    //
    // So the caller now speaks in three states, and `undefined` is the one that
    // preserves: it means "I could not read the command zone", which is the
    // only case where keeping the old value beats taking the new one. An
    // explicit `null` means "the source has no partner now" and DOES clear it.
    // Any LINKED save, not just an explicit refresh. An 'import'-mode call can
    // still resolve onto an EXISTING row through the sourceUrl match above (two
    // tabs importing the same URL, a stale decks array), and there
    // `requestedPartner` has already collapsed undefined to null - so the row's
    // real partner would be cleared by an import that simply could not read the
    // source's command zone. Unreachable through the picker today, which
    // excludes already-linked decks, but the safe reading costs nothing.
    // An instant save keeps the row's own partner.
    const partner = keep
      ? (zoneWrite ? zoneWrite.partner : (priorForKeep!.partner ?? null))
      : link
        ? (partnerName === undefined ? (prior?.partner ?? null) : requestedPartner)
        : requestedPartner

    // A full save's cloud payload (an instant save builds its own, below).
    const fullPayload = (userId: string): Record<string, unknown> => fullSavePayload({
      userId, commander, partner, bracket, engine, cardCount, decklist: usedDecklist, analysis,
      bumpRecency, ownership: opts?.ownership, link,
    })

    // An UNGUARDED link save (an import, or a refresh / link pull with no
    // captured base) is INSERT-ONLY. A row this account already has for the
    // URL (or the existingId row it would attach to) is never written over
    // without a compare-and-swap: a stale picker in another tab, or a deck
    // edited on another device since it was imported, would lose that edit.
    // A row that already holds exactly this write is this save (a repeat,
    // e.g. Save then Share); anything else is refused as stale and the
    // pulled list is held for the user's choice (lib/linkedRefresh), so
    // opening the deck asks "Use the {source} version" / "Keep my changes".
    if (link && !guardedLink && !opts?.forceNew) {
      const conflict = await linkedImportConflict({
        link, existingId: opts?.existingId, freshLocal, usedDecklist, payload: fullPayload,
        pending: usedDecklist !== undefined ? { analysis: analysisIn, decklist: usedDecklist, partner: partnerName, link } : null,
      })
      if (conflict) return conflict
    }

    // Always save locally first. Carry the prior row's ownership AND share/
    // Library state forward (a re-analyze must not reset 'saved' to 'owned',
    // and must not drop the "Listed in Library" indicator - the DB row keeps
    // those columns untouched, so the local object must too).
    const newDeck: SavedDeck = fence
      // A fence changes nothing it shows.
      ? { ...priorForKeep! }
      : keep
      ? {
          ...priorForKeep!, decklist: usedDecklist, cardCount, analysis,
          ...(zoneWrite ? { commander, partner } : {}),
          ...(metaWrite !== undefined ? { cardMeta: metaWrite } : {}),
        }
      : {
          id: targetId,
          commander,
          partner,
          bracket,
          engine,
          cardCount,
          decklist: usedDecklist,
          // Local mirror of the updated_at decision below, so the two sort keys
          // (savedAt for local/HomePage sorting, updated_at for the cloud load
          // order) can never disagree. A background save holds the prior row's
          // savedAt; with no prior row there is nothing to hold, and an insert
          // stamps anyway.
          savedAt: bumpRecency ? new Date().toISOString() : (prior?.savedAt ?? new Date().toISOString()),
          analysisVersion: CURRENT_ANALYSIS_VERSION,
          ownership: opts?.ownership ?? prior?.ownership,
          isPublic: prior?.isPublic,
          libraryListed: prior?.libraryListed,
          profileFeatured: prior?.profileFeatured,
          publicShareToken: prior?.publicShareToken,
          // Link state only changes when the caller supplies opts.link (import or
          // refresh); otherwise it carries forward untouched, same as ownership/
          // isPublic above. A refresh never overwrites a name the user has since
          // set - name only moves when the link payload itself includes one.
          name: link ? (link.name ?? prior?.name) : prior?.name,
          source: link ? link.source : prior?.source,
          sourceUrl: link ? link.sourceUrl : prior?.sourceUrl,
          sourceSyncedAt: link ? new Date().toISOString() : prior?.sourceSyncedAt,
          sourceDecklistHash: link ? link.decklistHash : prior?.sourceDecklistHash,
          // A full save never writes card_meta: the row it updates keeps its
          // own, so the local object does too (a new row has none).
          ...(prior?.cardMeta !== undefined ? { cardMeta: prior.cardMeta } : {}),
          analysis,
        }
    // The optimistic copy's content is not what any rev describes until the
    // database answers with the row (which carries its own rev).
    if (isCloud) delete newDeck.rev

    // Local mode writes the deck to localStorage HERE, synchronously, so the
    // result can say whether it landed. The write inside the setDecks updater
    // below is unchanged, but React may run that updater after this function
    // has already returned, so its outcome cannot be read from here. Both
    // write the same row into the same list; the later one wins as before.
    let persistedLocally = false
    let localFailure: { code: 'storage_full' | 'storage'; cause: unknown } | null = null
    let localRowMissing = false
    let localWritten: SavedDeck[] | null = null
    if (!isCloud) {
      if (freshLocal === null) {
        // eslint-disable-next-line i18next/no-literal-string -- a failure code; developer log only
        localFailure = { code: 'storage', cause: new Error('localStorage is not readable') }
      } else {
        const at = freshLocal.findIndex(d => d.id === targetId)
        // updateOnly never inserts: a row gone from storage stays gone.
        if (at >= 0 || !opts?.updateOnly) {
          const next = at >= 0 ? freshLocal.map((d, i) => (i === at ? newDeck : d)) : [newDeck, ...freshLocal]
          // ONE write of the post-compare list, built from the fresh read.
          localFailure = writeLocalDecks(next)
          persistedLocally = localFailure === null
          if (persistedLocally) localWritten = next
        } else {
          localRowMissing = true
        }
      }
    }

    // Guest: the list React shows is exactly the list just written (no second
    // persist from a possibly stale copy). A write that failed shows the deck
    // for this session only, unless the caller asked for a rollback.
    if (!isCloud) {
      if (localWritten) {
        setDecks(localWritten)
      } else if (!localRowMissing && !opts?.rollbackOnFailure) {
        setDecks(prev => {
          const idx = prev.findIndex(d => d.id === targetId)
          if (idx < 0 && opts?.updateOnly) return prev
          return idx >= 0 ? prev.map((d, i) => (i === idx ? newDeck : d)) : [newDeck, ...prev]
        })
        setCloudSyncError(tc('saved_decks.storage_full_session'))
      }
    } else setDecks(prev => {
      const idx = prev.findIndex(d => d.id === targetId)
      // updateOnly: a row removed between the check above and this update is
      // not put back.
      if (idx < 0 && opts?.updateOnly) return prev
      let updated: SavedDeck[]
      if (idx >= 0) {
        updated = [...prev]
        updated[idx] = newDeck
      } else {
        updated = [newDeck, ...prev]
      }
      // Cloud mode never persists to localStorage: the list holds the user's
      // cloud decks, which would contaminate storage for the next account.
      return updated
    })

    // The id we return + report. Cloud inserts may replace it with the
    // DB-assigned id below so callers can immediately edit/delete the new deck.
    let resolvedId = newDeck.id
    // True only when the Supabase write succeeded; the failure paths below all
    // leave it false so callers can tell a real saved_decks row from a local
    // temp id (cloudSyncError state is async and NOT readable right after await).
    let cloudSynced = false
    // The checked outcome of the cloud write (unset in local mode).
    let cloudOutcome: SaveOutcome | null = null
    let staleRow: SavedDeck | null = null

    // Also sync to cloud if signed in (retry once on failure)
    if (isCloud && supabase && user) {
      setCloudSyncError(null)
      const sb = supabase // capture for closure (TS null narrowing)

      type CloudAttempt = {
        error: string | null
        id?: string
        // The row as stored, when the write returned it (select('*')).
        row?: Partial<DbRow>
        // A compare-and-swap that wrote nothing.
        refused?: 'stale' | 'missing'
        fail?: { kind: 'retryable' } | { kind: 'fatal'; code: SaveFatalCode }
        cause?: unknown
        // A lost rev-guarded write that could not be settled (never retried
        // by the generic pass below).
        unknown?: UnknownSaveAttempt
        // Its identical retry was rejected: stop retrying, keep it.
        unknownFatal?: SaveFatalCode
        // An unguarded link save found an existing row it may not write.
        conflict?: boolean
        // The rev-guarded path ran its own retries: the generic pass skips it.
        final?: boolean
      }
      const failed = (error: { message?: string; code?: string } | null | undefined, status: number | undefined, id?: string): CloudAttempt => ({
        // eslint-disable-next-line i18next/no-literal-string -- developer log only (console), never shown
        error: error?.message ?? 'Cloud write failed',
        id,
        fail: classifyCloudError(error, status),
        cause: error,
      })

      // A guarded save (updateOnly + expectedDecklist) and every card_meta
      // merge: the rev compare-and-swap (the protocol is in the block above
      // readDeckRow). The decklist guard rides along too (belt and braces: a
      // rev that somehow does not describe the base list still cannot
      // overwrite it).
      const revGuarded = (!!opts?.updateOnly && opts.expectedDecklist !== undefined) || (keep && !!opts?.cardMetaMerge)
      const revGuardedWrite = async (id: string, deckPayload: Record<string, unknown>): Promise<CloudAttempt> => {
        const expected = opts?.expectedDecklist
        // The row already holds EXACTLY what this PATCH writes (every column).
        const holds = (r: Partial<DbRow>) => rowHoldsPayload(r, deckPayload)
        const readFailed = (read: Extract<DeckRead, { ok: false }>): CloudAttempt =>
          ({ ...failed(read.error, read.status, id), cause: read.cause, final: true })
        // eslint-disable-next-line i18next/no-literal-string -- refusal codes
        const refusedWith = (refused: 'stale' | 'missing', row?: Partial<DbRow>): CloudAttempt => ({ error: null, id, refused, row, final: true })
        // A fresh read taken BEFORE the write (a card_meta merge's, or the one
        // read an unknown rev needs): its rev is the guard, but only while it
        // still describes the edit's base. Moved past the caller's base rev
        // AND its list (or, with a zone write, its commander / partner) is
        // not the base any more: stale, never rebased onto it.
        const baseRev = typeof opts?.expectedRev === 'number' ? opts.expectedRev : undefined
        const mustConsume = !!opts?.mustConsumeRev
        const fromFreshRead = (r: Partial<DbRow>): CloudAttempt | number | undefined => {
          // A fence never takes the "already holds it" shortcut: only a real
          // update consumes the revision.
          if (!mustConsume && holds(r)) return { error: null, id, row: r, final: true }
          const moved = baseRev === undefined || revOf(r) !== baseRev
          if (moved) {
            // eslint-disable-next-line i18next/no-literal-string -- a refusal code
            if (expected !== undefined && (r.decklist ?? null) !== expected) return refusedWith('stale', r)
            if (zoneWrite) {
              // The SESSION's base zone (it travels with the save), never this
              // hook's current row; without it a zone write past its base is stale.
              const z = opts?.expectedCommandZone
              const k = (n: string | null | undefined) => (n ? frontKey(n.trim()) : '')
              // eslint-disable-next-line i18next/no-literal-string -- a refusal code
              if (!z || k(r.commander) !== k(z.commander) || k(r.partner) !== k(z.partner)) return refusedWith('stale', r)
            }
          }
          return revOf(r)
        }
        // N: the merge's own read (taken before this write), else the
        // caller's. Never the hook's current row: a row installed after the
        // caller's read (a background reload) would describe someone else's
        // newer write.
        let rev: number | undefined
        if (mergeRead) {
          const n = fromFreshRead(mergeRead)
          if (typeof n === 'object') return n
          rev = n
        } else if (baseRev !== undefined) {
          rev = baseRev
        } else {
          // 'fresh': only an instant save may establish its base here; a full
          // save (an analysis) must have read its base rev before analyzing.
          if (!keep) {
            console.warn('A full guarded save without a base rev is refused; read the base (readDeckBase) before analyzing.')
            // eslint-disable-next-line i18next/no-literal-string -- a refusal code
            return refusedWith('stale')
          }
          const read = await readDeckRow(sb, user.id, id)
          if (!read.ok) return readFailed(read)
          // eslint-disable-next-line i18next/no-literal-string -- a refusal code
          if (!read.row) return refusedWith('missing')
          const n = fromFreshRead(read.row)
          if (typeof n === 'object') return n
          rev = n
        }
        let definitiveRetry = false
        for (;;) {
          const res = await guardedUpdate(sb, user.id, { deckId: id, rev, expectedDecklist: expected, payload: deckPayload })
          if (res.error) {
            if (isAmbiguousFailure(res.error, res.status)) {
              // UNKNOWN: the payload and its guard are frozen from here on.
              if (rev === undefined) {
                // No rev to compete for (a row without the column): only a
                // row that already holds it settles this.
                const read = await readDeckRow(sb, user.id, id)
                if (read.ok && read.row && holds(read.row)) return { error: null, id, row: read.row, final: true }
                return { ...failed(res.error, res.status, id), cause: res.cause, final: true }
              }
              const attempt: UnknownSaveAttempt = {
                deckId: id, rev, payload: { ...deckPayload },
                ...(expected !== undefined ? { expectedDecklist: expected } : {}),
                ...(mustConsume ? { mustConsumeRev: true as const } : {}),
              }
              const settled = await resolveAttempt(sb, user.id, attempt)
              switch (settled.kind) {
                case 'saved': return { error: null, id, row: settled.row, final: true }
                // eslint-disable-next-line i18next/no-literal-string -- a refusal code
                case 'stale': return refusedWith('stale', settled.row)
                // eslint-disable-next-line i18next/no-literal-string -- a refusal code
                case 'missing': return refusedWith('missing')
                case 'unknown':
                  // eslint-disable-next-line i18next/no-literal-string -- developer log only (console), never shown
                  return { error: 'Cloud write outcome unknown', id, unknown: attempt, unknownFatal: settled.fatalCode, cause: settled.cause, final: true }
              }
            }
            // A 408 / 429 never ran: the same request once more.
            if (classifyCloudError(res.error, res.status).kind === 'retryable' && !definitiveRetry) {
              definitiveRetry = true
              await new Promise(r => setTimeout(r, 1000))
              continue
            }
            return { ...failed(res.error, res.status, id), final: true }
          }
          const hit = res.rows?.[0]
          if (hit) return { error: null, id, row: hit, final: true }
          // Zero rows: the row moved past N. Read it as it is NOW: holding
          // exactly this write is saved, anything else is stale. Never
          // retried against the new rev.
          const read = await readDeckRow(sb, user.id, id)
          if (!read.ok) return readFailed(read)
          // eslint-disable-next-line i18next/no-literal-string -- a refusal code
          if (!read.row) return refusedWith('missing')
          // Held past N (someone consumed N with this very content) is saved;
          // a fence held AT N consumed nothing.
          if (holds(read.row) && !(mustConsume && revOf(read.row) === rev)) return { error: null, id, row: read.row, final: true }
          // eslint-disable-next-line i18next/no-literal-string -- a refusal code
          return refusedWith('stale', read.row)
        }
      }

      const attemptCloudSync = async (): Promise<CloudAttempt> => {
        // Refresh the session to ensure we have a valid JWT
        const { data: { session } } = await sb.auth.getSession()
        // eslint-disable-next-line i18next/no-literal-string -- developer log only (console), never shown
        if (!session) return { error: 'Not signed in - deck saved locally only.', fail: { kind: 'fatal', code: 'auth' } }

        const deckPayload: Record<string, unknown> = fence
          // The fence: the list column to the value it holds (a no-op the
          // trigger still counts as an update).
          ? { decklist: opts?.expectedDecklist ?? null }
          : keep
          ? {
              // ONLY the list and its count (and, when the editor moved the
              // command zone, commander + partner). Never the analysis.
              decklist: usedDecklist ?? null,
              card_count: cardCount,
              ...(zoneWrite ? { commander, partner } : {}),
              // And, when the editor sent it, card_meta (same update).
              ...(metaWrite !== undefined ? { card_meta: metaWrite } : {}),
            }
          : fullPayload(user.id)

        // Every INSERT below writes this instead of deckPayload. A brand-new
        // row is always user-initiated (there is no prior row for a background
        // self-heal to have found), and a row inserted with no updated_at
        // sorts below every stamped row in the load query - so the opt-out is
        // deliberately UPDATE-only. When bumpRecency is true the two payloads
        // are the same object.
        const insertPayload: Record<string, unknown> = bumpRecency
          ? deckPayload
          : { ...deckPayload, updated_at: new Date().toISOString() }

        // Linked saves dedup on sourceUrl instead of commander+partner or a
        // bare existingId - three Atraxa brews imported from Archidekt are
        // three decks, so matching one to another by commander would
        // silently collapse them. Resolution order deliberately MIRRORS the
        // local targetId computation above exactly (sourceUrl match wins,
        // existingId is the fallback, otherwise it's a new row), so this
        // branch is checked BEFORE the plain existingId fast path below - a
        // caller passing both must never have local state update one row
        // while the cloud write updates a different one.
        // A guarded link save (a linked-source refresh / link pull of a known
        // row, see guardedLink): the same rev compare-and-swap as every other
        // guarded save, on exactly its existingId row. Stale means the deck
        // changed since the refresh started; the caller asks the user.
        if (guardedLink && revGuarded) return await revGuardedWrite(opts!.existingId!, deckPayload)
        if (link) {
          // An unguarded link save is INSERT-ONLY (see linkedImportConflict,
          // which ran before anything was shown). A row for this URL that
          // appeared since (another tab, a double click) is never updated
          // here either: holding exactly this write it is this save, else it
          // is refused as stale and held for the user's choice.
          const existingConflict = async (id: string): Promise<CloudAttempt> => {
            const read = await readDeckRow(sb, user.id, id)
            if (!read.ok) return { ...failed(read.error, read.status, id), cause: read.cause, final: true }
            // eslint-disable-next-line i18next/no-literal-string -- a refusal code
            if (!read.row) return { error: null, id, refused: 'missing', final: true }
            if (rowHoldsPayload(read.row, deckPayload)) return { error: null, id, row: read.row }
            // eslint-disable-next-line i18next/no-literal-string -- a refusal code
            return { error: null, id, refused: 'stale', row: read.row, final: true, conflict: true }
          }
          const { data: existingByUrl } = await sb
            .from('saved_decks')
            .select('id')
            .eq('user_id', user.id)
            .eq('source_url', link.sourceUrl)
            .limit(1)

          if (existingByUrl && existingByUrl.length > 0) return existingConflict(existingByUrl[0].id)
          if (opts?.existingId) return existingConflict(opts.existingId)

          // Dedupe guard: check one more time right before insert. Mirrors
          // the recheck the commander+partner path below already does, and
          // is NOT optional here - idx_saved_decks_user_source_url (the
          // migration's index) is a plain btree, not a unique constraint, so
          // nothing at the DB layer stops two concurrent saves for the same
          // sourceUrl (a double-clicked import button, two open tabs, a
          // retry racing an in-flight first attempt) from both seeing no
          // match and both inserting - exactly the duplicate this dedup
          // exists to prevent.
          const { data: recheckByUrl } = await sb
            .from('saved_decks')
            .select('id')
            .eq('user_id', user.id)
            .eq('source_url', link.sourceUrl)
            .limit(1)
          if (recheckByUrl && recheckByUrl.length > 0) return existingConflict(recheckByUrl[0].id)

          const { data: inserted, error, status } = await runDeckWrite(() => sb
            .from('saved_decks')
            .insert(insertPayload)
            .select('id')
            .single())

          if (error) {
            // 23505 = unique_violation. The recheck above is a best-effort
            // guard, not a lock - it still leaves a race window between the
            // recheck and this insert (two concurrent requests can both
            // recheck-miss, then both insert). idx_saved_decks_user_source_url_uniq
            // (2026-08-05 migration) closes that window at the database
            // layer: the losing insert now fails with 23505 instead of
            // silently creating a duplicate row. On 23505 specifically,
            // converge on the winner's row the same way the recheck branch
            // above does - re-select the row for this (user_id, source_url)
            // and settle on it (the same write: saved; else stale, never
            // updated blind), so the losing tab ends up pointed at the same
            // row instead of surfacing an error to the user. Any other
            // error still propagates as a failure below.
            if (error.code === '23505') {
              const { data: winner } = await sb
                .from('saved_decks')
                .select('id')
                .eq('user_id', user.id)
                .eq('source_url', link.sourceUrl)
                .limit(1)
              if (winner && winner.length > 0) return existingConflict(winner[0].id)
              // Constraint violation implies a matching row exists; if the
              // re-select still can't find it, surface the original error
              // rather than silently no-op.
            }
            return failed(error, status)
          }

          return { error: null, id: (inserted as { id?: string } | null)?.id }
        }

        // Inline-edit fast path: PATCH the exact row by id (scoped to the user)
        // instead of the commander+partner dedup, so editing a deck that shares
        // a commander updates the right row and never spawns a duplicate.
        // Only reached when opts.link is absent - see the link branch above.
        if (opts?.existingId && revGuarded) return await revGuardedWrite(opts.existingId, deckPayload)
        if (opts?.existingId) {
          const id = opts.existingId
          // select('*') on an instant save, so the caller gets the row as
          // STORED. Otherwise select('id'), so an RLS-blocked / not-found
          // PATCH (which PostgREST reports as success with zero rows)
          // surfaces as an error instead of a silent no-op.
          // eslint-disable-next-line i18next/no-literal-string -- Supabase columns
          const cols = keep ? '*' : 'id'
          const { data: updated, error, status } = await runDeckWrite(() => sb
            .from('saved_decks')
            .update(deckPayload)
            .eq('id', id)
            .eq('user_id', user.id)
            .select(cols))
          if (error) return failed(error, status, id)
          if (!updated || updated.length === 0) {
            return {
              // eslint-disable-next-line i18next/no-literal-string -- developer log only (console), never shown
              error: 'Update affected no rows (deck not found or not permitted).',
              id,
              // eslint-disable-next-line i18next/no-literal-string -- a refusal code
              refused: checked ? 'missing' : undefined,
            }
          }
          return { error: null, id, row: cols === '*' ? (updated as Partial<DbRow>[])[0] : undefined }
        }

        // Copy path: skip the commander+partner dedup entirely and insert. The
        // local branch above already refused to reuse a prior row, so matching
        // one here would UPDATE someone else's deck with the copied list.
        if (opts?.forceNew) {
          const { data: copied, error, status } = await runDeckWrite(() => sb
            .from('saved_decks')
            .insert(insertPayload)
            .select('id')
            .single())
          return error ? failed(error, status) : { error: null, id: (copied as { id?: string } | null)?.id }
        }

        // Find existing deck for this commander+partner, then update or insert.
        // Null-aware: inserts store commander NULL for unknown commanders, so
        // the dedupe lookup must match with .is(), not eq('') (which never
        // matches NULL and would insert a duplicate row per save). The
        // null-commander key includes the decklist so distinct unknown-
        // commander decks never merge; without a decklist there is no safe
        // content key, so those saves always insert.
        let query = sb
          .from('saved_decks')
          .select('id')
          .eq('user_id', user.id)
        query = commander
          // eslint-disable-next-line i18next/no-literal-string -- Supabase table, column or filter
          ? query.eq('commander', commander)
          // eslint-disable-next-line i18next/no-literal-string -- Supabase table, column or filter
          : query.is('commander', null).eq('decklist', usedDecklist ?? '')
        if (partner) {
          // eslint-disable-next-line i18next/no-literal-string -- Supabase table, column or filter
          query = query.eq('partner', partner)
        } else {
          // eslint-disable-next-line i18next/no-literal-string -- Supabase table, column or filter
          query = query.is('partner', null)
        }
        const { data: existing } = await query.limit(1)

        if (existing && existing.length > 0) {
          const { error, status } = await runDeckWrite(() => sb
            .from('saved_decks')
            .update(deckPayload)
            .eq('id', existing[0].id))
          return error ? failed(error, status, existing[0].id) : { error: null, id: existing[0].id }
        }

        // Dedupe guard: check one more time right before insert
        let recheckQuery = sb
          .from('saved_decks')
          .select('id')
          .eq('user_id', user.id)
        recheckQuery = commander
          // eslint-disable-next-line i18next/no-literal-string -- Supabase table, column or filter
          ? recheckQuery.eq('commander', commander)
          // eslint-disable-next-line i18next/no-literal-string -- Supabase table, column or filter
          : recheckQuery.is('commander', null).eq('decklist', usedDecklist ?? '')
        if (partner) {
          // eslint-disable-next-line i18next/no-literal-string -- Supabase table, column or filter
          recheckQuery = recheckQuery.eq('partner', partner)
        } else {
          // eslint-disable-next-line i18next/no-literal-string -- Supabase table, column or filter
          recheckQuery = recheckQuery.is('partner', null)
        }
        const { data: recheck } = await recheckQuery.limit(1)
        if (recheck && recheck.length > 0) {
          const { error, status } = await runDeckWrite(() => sb
            .from('saved_decks')
            .update(deckPayload)
            .eq('id', recheck[0].id))
          return error ? failed(error, status, recheck[0].id) : { error: null, id: recheck[0].id }
        }

        // Insert a brand-new row and capture the DB-assigned id so the local
        // record can adopt it - otherwise edit/delete/share right after a save
        // would target the temporary local id and silently no-op under RLS.
        const { data: inserted, error, status } = await runDeckWrite(() => sb
          .from('saved_decks')
          .insert(insertPayload)
          .select('id')
          .single())
        return error ? failed(error, status) : { error: null, id: (inserted as { id?: string } | null)?.id }
      }

      // A thrown write (fetch rejected, a timeout) is a network failure.
      const attemptSafely = async (): Promise<CloudAttempt> => {
        try {
          return await attemptCloudSync()
        } catch (err) {
          // eslint-disable-next-line i18next/no-literal-string -- developer log only (console), never shown
          return { error: err instanceof Error ? err.message : 'Cloud write threw', fail: { kind: 'retryable' }, cause: err }
        }
      }

      let result = await attemptSafely()
      // Retry once after a brief delay (covers transient errors, token refresh).
      // deck_busy is handled a layer down, per WRITE, by runDeckWrite: by the
      // time an error reaches here it has already survived its 5s retry, so
      // this generic pass is unchanged and stays what it always was. A
      // refusal is never retried (it would be refused again), and the checked
      // path does not retry what retrying cannot fix.
      if (result.error && !result.refused && !result.final && (!checked || result.fail?.kind !== 'fatal')) {
        console.warn('Cloud save attempt 1 failed, retrying:', result.error)
        await new Promise(r => setTimeout(r, 1000))
        result = await attemptSafely()
      }
      if (result.unknown) {
        console.warn('Cloud save outcome unknown; holding the attempt to resolve:', result.error)
        // eslint-disable-next-line i18next/no-literal-string -- an outcome code
        cloudOutcome = { status: 'unknown', attempt: result.unknown, cause: result.cause ?? result.error, ...(result.unknownFatal ? { fatalCode: result.unknownFatal } : {}) }
      } else if (result.refused) {
        cloudOutcome = { status: result.refused }
        // Reconcile: a stale refusal returns the row as stored; this list
        // shows it rather than its outdated copy (no write is made).
        if (result.refused === 'stale' && result.row && result.conflict && result.id) {
          // A link save that found a row it may not write (a race past
          // linkedImportConflict): the optimistic copy goes, the stored row
          // shows, and the pulled list is held for the user's choice.
          const conflictDeck = deckFromDbRow(result.row, decksRef.current.find(d => d.id === result.id) ?? { ...newDeck, id: result.id })
          const tempId = targetId
          bumpEpoch()
          setDecks(prev => {
            const rest = tempId !== conflictDeck.id ? prev.filter(d => d.id !== tempId) : prev
            return rest.some(d => d.id === conflictDeck.id) ? rest.map(d => (d.id === conflictDeck.id ? conflictDeck : d)) : [conflictDeck, ...rest]
          })
          if (link) holdLinkedChoice(conflictDeck.id, link, usedDecklist !== undefined ? { analysis: analysisIn, decklist: usedDecklist, partner: partnerName, link } : null)
          resolvedId = conflictDeck.id
        } else if (result.refused === 'stale' && result.row) staleRow = deckFromDbRow(result.row, prior ?? newDeck)
      } else if (result.error) {
        setCloudSyncError(tc('saved_decks.cloud_sync_failed'))
        console.error('Cloud save failed after retry:', result.error)
        cloudOutcome = result.fail?.kind === 'fatal'
          // eslint-disable-next-line i18next/no-literal-string -- an outcome code
          ? { status: 'fatal_error', code: result.fail.code, cause: result.cause ?? result.error }
          // eslint-disable-next-line i18next/no-literal-string -- an outcome code
          : { status: 'retryable_error', cause: result.cause ?? result.error }
      } else {
        cloudSynced = true
        if (result.id && result.id !== resolvedId) {
          // Adopt the DB-assigned id so later edit/delete/share hit the right row.
          // Capture the temp id in a const BEFORE reassigning resolvedId, so the
          // (deferred) setDecks callback matches the old id, not the new one.
          const tempId = resolvedId
          const dbId = result.id
          // A row this list already holds under the DB id (a converged link
          // save) keeps its place; the temporary copy goes.
          setDecks(prev => (prev.some(d => d.id === dbId) ? prev.filter(d => d.id !== tempId) : prev.map(d => (d.id === tempId ? { ...d, id: dbId } : d))))
          // The row's newest-attempt entry follows it to the adopted id.
          const latest = contentAttemptRef.current.get(tempId)
          if (latest !== undefined) {
            contentAttemptRef.current.delete(tempId)
            contentAttemptRef.current.set(dbId, latest)
          }
          resolvedId = dbId
        }
        // The row as the database holds it, when the write returned one. For
        // a converged compare-and-swap that is ANOTHER tab's row (its
        // analysis, not this write's): the database is the truth either way.
        const storedDeck = result.row ? deckFromDbRow(result.row, { ...newDeck, id: resolvedId }) : { ...newDeck, id: resolvedId }
        if (result.row) install(storedDeck)
        // eslint-disable-next-line i18next/no-literal-string -- an outcome code
        cloudOutcome = { status: 'saved', deck: storedDeck }
      }
    }

    // Put back the CONTENT this save replaced, only while it is still the
    // newest content save of the row (a later save of the same deck is never
    // undone), and only when a prior row existed to restore. Content fields
    // only: a rename, share or ownership change made while this write was in
    // flight clones the row and must survive the rollback (restoring by object
    // identity silently skipped exactly that case).
    // And only over THIS attempt's optimistic content: a row another
    // instance landed (installed by broadcast) or the stored row reconciled
    // after a refusal is the truth, never rolled back to the pre-save copy.
    const stillOptimistic = (d: SavedDeck | undefined) => !!d && d.decklist === newDeck.decklist && d.analysis === newDeck.analysis
    // This attempt still owns the row: it is the newest content save AND the
    // list still shows its optimistic copy.
    const ownsRow = contentAttemptRef.current.get(targetId) === attempt
      && stillOptimistic(decksRef.current.find(d => d.id === targetId))
    if (staleRow) {
      // A stale refusal read the stored row back. It replaces ONLY this
      // attempt's own optimistic row (instead of a rollback to the outdated
      // pre-save copy); when something newer already took the row (a
      // broadcast retired the attempt), the list re-reads instead of
      // installing an older read.
      bumpEpoch()
      if (ownsRow) install(staleRow)
      else requestReload()
    } else if (opts?.rollbackOnFailure && prior && !cloudSynced && !persistedLocally && ownsRow) {
      const restore = {
        ...contentFields(prior),
      }
      // A link save also put its link stamp on the optimistic row; back with
      // the content it describes, or the restored list reads as a local
      // edit against the new hash (a false "you edited this deck").
      // Only while the row still carries THIS attempt's link stamp: an unlink
      // (or another link) made meanwhile is never undone by the rollback.
      const ownStamp = (d: SavedDeck) => !!link && d.source === newDeck.source && d.sourceUrl === newDeck.sourceUrl
        && d.sourceSyncedAt === newDeck.sourceSyncedAt && d.sourceDecklistHash === newDeck.sourceDecklistHash
        && (!link.name || d.name === newDeck.name)
      setDecks(prev => prev.map(d => {
        if (d.id !== targetId) return d
        const linkBack = link && ownStamp(d) ? {
          source: prior.source, sourceUrl: prior.sourceUrl, sourceSyncedAt: prior.sourceSyncedAt,
          sourceDecklistHash: prior.sourceDecklistHash, ...(link.name ? { name: prior.name } : {}),
        } : {}
        return { ...d, ...restore, ...linkBack }
      }))
    }
    // Settled: drop this attempt's entry if it is still the row's newest (a
    // newer attempt owns the entry otherwise), under either id.
    for (const key of [targetId, resolvedId]) {
      if (contentAttemptRef.current.get(key) === attempt) contentAttemptRef.current.delete(key)
    }

    // Every guarded write that landed is heard by the OTHER mounted lists of
    // this scope, so one tab never shows two versions of a deck.
    const landedRow = outcomePreview(cloudOutcome, persistedLocally, newDeck)
    if (landedRow && opts?.existingId && opts.updateOnly && opts.expectedDecklist !== undefined) {
      // This list's own landing counts too: a load of this instance already
      // out read before it.
      bumpEpoch()
      // eslint-disable-next-line i18next/no-literal-string -- a scope code
      broadcastPersisted(user?.id ?? 'guest', landedRow, instanceIdRef.current)
    }
    const legacyRefused = cloudOutcome?.status === 'stale' || cloudOutcome?.status === 'missing' ? cloudOutcome.status : undefined
    const result: SaveDeckResult = {
      id: resolvedId,
      cloudSynced,
      isCloud,
      persistedLocally,
      ...(legacyRefused ? { refused: legacyRefused } : {}),
    }
    let outcome: SaveOutcome
    if (cloudOutcome) outcome = cloudOutcome
    // eslint-disable-next-line i18next/no-literal-string -- an outcome code
    else if (persistedLocally) outcome = { status: 'saved', deck: newDeck }
    // eslint-disable-next-line i18next/no-literal-string -- an outcome code
    else if (localRowMissing) outcome = { status: 'missing' }
    // eslint-disable-next-line i18next/no-literal-string -- an outcome code
    else if (localFailure) outcome = { status: 'fatal_error', code: localFailure.code, cause: localFailure.cause }
    // eslint-disable-next-line i18next/no-literal-string -- an outcome code, developer log only
    else outcome = { status: 'retryable_error', cause: new Error('Deck save did not persist') }
    return { result, outcome }
  }
  // The latest render's implementation, so writeDeck stays one stable callback.
  useLayoutEffect(() => {
    // eslint-disable-next-line i18next/no-literal-string -- a scope code
    committedWriterRef.current = { impl: writeDeckUnlockedImpl, isCloud, scope: user?.id ?? 'guest' }
  })

  const saveDeck = useCallback<SaveDeckFn>(
    async (analysis, decklistText, partnerName, opts) => (await writeDeck(analysis, decklistText, partnerName, opts, false)).result,
    [writeDeck],
  )

  const saveDeckChecked = useCallback<SaveDeckCheckedFn>(
    async (analysis, decklistText, partnerName, opts) => (await writeDeck(analysis, decklistText, partnerName, opts, true)).outcome,
    [writeDeck],
  )

  /**
   * New deck (lib/deckDraft): `analysis` is the REAL /decks/analyze-complete
   * answer for `decklist` (the command zone, plus any cards the user already
   * has). It is stored marked as a draft, ALWAYS as a new row (forceNew: a
   * fresh id, never the commander dedup, no share / link / source state from
   * any other row), owned by the user. Guests land in localStorage.
   */
  const saveDraftDeck = useCallback(
    (draft: { analysis: CompleteAnalysis; decklist: string; partner: string | null; scope?: string }): Promise<SaveDeckResult> =>
      saveDeck(markDraft(draft.analysis, draft.analysis.deck_stats?.total_cards ?? countDecklistCards(draft.decklist)), draft.decklist, draft.partner, {
        forceNew: true,
        // eslint-disable-next-line i18next/no-literal-string -- an ownership code
        ownership: 'owned',
        // Started in another session (New deck's analysis awaited across a
        // sign-out or sign-in): refused, nothing written.
        ...(draft.scope !== undefined ? { expectedScope: draft.scope } : {}),
      }),
    [saveDeck],
  )

  /**
   * The storage scope a save would write to NOW: the account id, or 'guest'
   * (null before the first commit). Read it before an await and pass it as
   * SaveDeckOptions.expectedScope (saveDraftDeck's `scope`).
   */
  const currentScope = useCallback((): string | null => committedWriterRef.current?.scope ?? null, [])

  /**
   * Put a row the deck editor's save queue already persisted into THIS
   * instance's list (a remount's fresh hook, whose load may predate the
   * write). A row this list does not hold is not inserted.
   */
  const applyPersistedDeck = useCallback((deck: SavedDeck) => {
    setDecks(prev => (prev.some(d => d.id === deck.id) ? prev.map(d => (d.id === deck.id ? deck : d)) : prev))
  }, [setDecks])

  // Rows another mounted instance of this scope landed (the editor's save
  // queue outlives the screens that start its writes). Installing one also
  // retires THIS instance's pending content attempt on that deck, so its
  // failure can never roll the landed row back.
  // eslint-disable-next-line i18next/no-literal-string -- a scope code
  const editorScope = user?.id ?? 'guest'
  useEffect(() => subscribePersistedDecks((deck, scope, source) => {
    // Every landed row of this account also updates the offline copy (W3).
    if (scope === editorScope && user && isCloud && offlineRuntimeActive()) void storeDeckSnapshot(user.id, [deck], { complete: false })
    if (scope !== editorScope || source === instanceIdRef.current) return
    contentAttemptRef.current.delete(deck.id)
    // A load in flight read before this landed: the epoch makes it re-read
    // (once) instead of installing. Otherwise install the row now.
    bumpEpoch()
    if (!loadInFlightRef.current) applyPersistedDeck(deck)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bumpEpoch only touches a ref
  }), [editorScope, applyPersistedDeck])

  // Offline contracts C1 / C2: back online, or the session restored, re-read
  // the list (coalesced with any load in flight).
  useEffect(() => {
    if (!isCloud) return
    const offs = [
      onConnectivityEvent(e => { if (e === CONN_EVENT.recovered) requestReload() }),
      onIdentityEmit(e => { if (e === IDENTITY_EMIT.sessionRestored) requestReload() }),
    ]
    return () => { for (const off of offs) off() }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the helpers only touch refs
  }, [isCloud])

  // Guest: another TAB wrote the saved list. Re-read it (coalesced with any
  // load in flight), so this tab never shows its stale copy.
  useEffect(() => {
    if (isCloud || typeof window === 'undefined') return
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return
      bumpEpoch()
      requestReload()
    }
    // eslint-disable-next-line i18next/no-literal-string -- a DOM event name
    window.addEventListener('storage', onStorage)
    // eslint-disable-next-line i18next/no-literal-string -- a DOM event name
    return () => window.removeEventListener('storage', onStorage)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the helpers only touch refs
  }, [isCloud])

  // Cloud: the deck-box runner wrote this account's saved decks on the
  // server (lib/savedDecksRemoteSignal). Re-read, coalesced with any load.
  const cloudUserId = isCloud ? user?.id ?? null : null
  useEffect(() => {
    if (!cloudUserId) return
    return onSavedDecksChangedRemotely((uid) => {
      if (uid !== cloudUserId) return
      bumpEpoch()
      requestReload()
    })
  }, [cloudUserId])

  // True once the delete landed (the deck-box delete's phase 2 needs to know).
  const removeDeck = useCallback(async (id: string): Promise<boolean> => {
    // A removed row has no content saves to roll back.
    contentAttemptRef.current.delete(id)
    if (isCloud && supabase) {
      // .select() forces Supabase to return the affected rows. Without
      // it, RLS denials look identical to successful deletes - error is
      // null and zero rows are affected, but the client thinks it
      // worked. The optimistic local filter then hides the row until
      // the next refresh pulls it back from the server, which looks to
      // the user like delete is broken on both web and iOS.
      const sb = supabase
      const { data, error } = await runDeckWrite(() => sb
        .from('saved_decks')
        .delete()
        .eq('id', id)
        .select('id'))

      if (error) {
        console.error('Failed to delete deck:', error)
        setCloudSyncError(tc('saved_decks.delete_failed', { message: error.message }))
        return false
      }
      if (!data || data.length === 0) {
        // RLS denied silently. Most likely cause: row's user_id doesn't
        // match the current session (stale row from before the account-
        // boundary fix in commit 9092a76). Surface it instead of
        // pretending the delete worked.
        setCloudSyncError(tc('saved_decks.delete_blocked'))
        return false
      }

      // (setDecks bumps the epoch: a load already out re-reads.)
      setDecks(prev => {
        const updated = prev.filter(d => d.id !== id)
        // Don't write the cloud list to localStorage; it would contaminate
        // local storage with this user's cloud decks for the next sign-in.
        return updated
      })
      // A deleted ManaBox deck leaves its deck box dangling on the shelf.
      invalidateBuiltDecks()
      // A deleted My Deck no longer needs its copies (spare binders).
      if (user) decksChangedRef.current(user.id)
      return true
    } else {
      // Guest: the one locked read / modify / write, on the FRESH list, so a
      // deck another tab added survives.
      if (!(await mutateGuestDecks(list => list.filter(d => d.id !== id)))) {
        setCloudSyncError(tc('saved_decks.storage_full'))
        return false
      }
      return true
    }
  }, [isCloud, user, runDeckWrite, setDecks, mutateGuestDecks])

  // Move a deck between the My Decks (owned) and Saved buckets - an
  // ownership-only update. Optimistic, with rollback: cloud failures (incl. a
  // silent RLS-blocked zero-row PATCH) revert React state; local-mode storage
  // failures revert by not applying the change.
  const reclassifyDeck = useCallback(async (id: string, ownership: DeckOwnership) => {
    const prevOwnership = decksRef.current.find(d => d.id === id)?.ownership

    if (!isCloud) {
      if (!(await mutateGuestDecks(list => list.map(d => (d.id === id ? { ...d, ownership } : d))))) {
        setCloudSyncError(tc('saved_decks.storage_full'))
      }
      return
    }

    // Cloud: optimistic update, then verify the PATCH actually hit a row.
    setDecks(prev => prev.map(d => (d.id === id ? { ...d, ownership } : d)))
    if (supabase && user) {
      const sb = supabase
      // deck_busy keeps the optimistic edit on screen and retries; only a
      // second failure rolls it back and speaks.
      const { data, error } = await runDeckWrite(() => sb
        .from('saved_decks')
        .update({ ownership })
        .eq('id', id)
        .eq('user_id', user.id)
        .select('id'))
      if (error || !data || data.length === 0) {
        // Roll back React state only (cloud mode never writes localStorage).
        setDecks(prev => prev.map(d => (d.id === id ? { ...d, ownership: prevOwnership } : d)))
        setCloudSyncError(error?.message ?? tc('saved_decks.move_failed'))
      } else {
        // Saved <-> My Decks moves the deck's needs in or out of the spare rule.
        decksChangedRef.current(user.id)
      }
    }
  }, [isCloud, user, runDeckWrite, setDecks, mutateGuestDecks])

  // Rename is optimistic with rollback, mirroring reclassifyDeck exactly (same
  // local-mode compute-then-persist-then-commit shape, same cloud-mode
  // optimistic-update-then-verify-then-rollback shape). An empty or
  // whitespace-only name is stored as NULL, never as '', so deckTitle's
  // fallback to the commander stays the single source of truth for "unnamed" -
  // a stored '' would permanently blank the title instead of falling back.
  /**
   * Re-read ONE deck row for the current owner and replace the in-memory copy
   * (plan card-sheet-actions-2026-10-17, section 6: an Add to deck from search
   * applies on top of the row as stored NOW, never on a stale list). Resolves
   * the fresh deck, or null when the row is gone (deleted elsewhere), in which
   * case it also leaves the list. Throws when the row could not be read.
   * Owner-scoped: the cloud read filters by user_id; a guest reads the local
   * store. A list change meanwhile is fine: the row is merged by id.
   */
  const refreshDeck = useCallback(async (id: string): Promise<SavedDeck | null> => {
    // This hook's own useAuth may settle a render after the caller's (each
    // useAuth() keeps its own state): until it has, `isCloud` is false for a
    // signed-in user, and the guest store below would answer null, which the
    // caller reads as "the deck was deleted". Throw instead (a retryable read
    // failure), never a false missing.
    // eslint-disable-next-line i18next/no-literal-string -- developer message, never shown
    if (authLoading) throw new Error('auth not settled')
    if (!isCloud) {
      const fresh = readLocalDecksStrict()
      // eslint-disable-next-line i18next/no-literal-string -- developer message, never shown
      if (fresh === null) throw new Error('saved decks unreadable')
      const row = fresh.find(d => d.id === id) ?? null
      setDecks(prev => row
        ? (prev.some(d => d.id === id) ? prev.map(d => (d.id === id ? row : d)) : [row, ...prev])
        : prev.filter(d => d.id !== id))
      return row
    }
    // eslint-disable-next-line i18next/no-literal-string -- developer message, never shown
    if (!supabase || !user) throw new Error('not signed in')
    const { data, error } = await supabase
      .from('saved_decks')
      .select('*')
      .eq('id', id)
      .eq('user_id', user.id)
      .maybeSingle()
    if (error) throw error
    const row = data ? mapRowToDeck(data as DbRow) : null
    setDecks(prev => row
      ? (prev.some(d => d.id === id) ? prev.map(d => (d.id === id ? row : d)) : [row, ...prev])
      : prev.filter(d => d.id !== id))
    // The stored row differs from the last trusted one (written elsewhere):
    // My Decks changed under this tab.
    noteServerDeckRow(user.id, id, row)
    return row
  }, [authLoading, isCloud, user, setDecks])

  /**
   * The base a full save must hold BEFORE it analyzes, when the caller has
   * no rev for its row: the list, rev and command zone as stored now.
   * Resolves null when the row is gone, undefined for a guest (no revs).
   * Throws when the row could not be read.
   */
  const readDeckBase = useCallback(async (id: string): Promise<DeckBase | null | undefined> => {
    if (!isCloud || !supabase || !user) return undefined
    const read = await readDeckRow(supabase, user.id, id)
    // eslint-disable-next-line i18next/no-literal-string -- developer message, never shown
    if (!read.ok) throw (read.cause ?? new Error('saved deck unreadable'))
    if (!read.row) return null
    const rev = revOf(read.row)
    // eslint-disable-next-line i18next/no-literal-string -- developer message, never shown
    if (rev === undefined) throw new Error('saved deck has no rev')
    return {
      decklist: read.row.decklist ?? null, rev, commander: read.row.commander ?? null, partner: read.row.partner ?? null,
      row: mapRowToDeck({ ...(read.row as DbRow), created_at: read.row.created_at ?? new Date(0).toISOString() }),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- readDeckRow only closes over runDeckWrite-free helpers
  }, [isCloud, user])

  /**
   * Did a refresh write land? The row (fresh: the cloud row, or the guest's
   * stored row) holds EVERY column that write sets: the list, its count, the
   * command zone, bracket and engine, the analysis and the link stamp. The
   * same payload and the same comparison (rowHoldsPayload) the write itself
   * uses, never the text alone. null: the deck is gone. Throws when the row
   * could not be read.
   */
  const confirmRefreshLanded = useCallback(async (
    id: string,
    w: { analysis: CompleteAnalysis; decklist: string; partner: string | null | undefined; link: NonNullable<SaveDeckOptions['link']> },
  ): Promise<boolean | null> => {
    let row: Partial<DbRow>
    // eslint-disable-next-line i18next/no-literal-string -- a scope code
    let userId = 'guest'
    if (!isCloud) {
      const fresh = readLocalDecksStrict()
      // eslint-disable-next-line i18next/no-literal-string -- developer message, never shown
      if (fresh === null) throw new Error('saved decks unreadable')
      const d = fresh.find(x => x.id === id)
      if (!d) return null
      row = deckAsRow(d, userId)
    } else {
      // eslint-disable-next-line i18next/no-literal-string -- developer message, never shown
      if (!supabase || !user) throw new Error('not signed in')
      const read = await readDeckRow(supabase, user.id, id)
      // eslint-disable-next-line i18next/no-literal-string -- developer message, never shown
      if (!read.ok) throw (read.cause ?? new Error('saved deck unreadable'))
      if (!read.row) return null
      row = read.row
      userId = user.id
    }
    const analysis: CompleteAnalysis = { ...w.analysis, analyzed_content_hash: analysisContentHash(w.decklist) }
    const derived = savedDeckFields(analysis)
    const payload = fullSavePayload({
      userId, commander: derived.commander,
      // A refresh that could not read its partner kept the row's.
      partner: w.partner === undefined ? (row.partner ?? null) : w.partner,
      bracket: derived.bracket, engine: derived.engine, cardCount: derived.cardCount,
      decklist: w.decklist, analysis, bumpRecency: true, link: w.link,
    })
    return rowHoldsPayload(row, payload)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- readDeckRow only closes over runDeckWrite-free helpers
  }, [isCloud, user])

  const renameDeck = useCallback(async (id: string, name: string | null) => {
    const clean = name?.trim() ? name.trim() : null

    if (!isCloud) {
      if (!(await mutateGuestDecks(list => list.map(d => (d.id === id ? { ...d, name: clean } : d))))) {
        setCloudSyncError(tc('saved_decks.storage_full'))
      }
      return
    }

    const prevName = decksRef.current.find(d => d.id === id)?.name
    setDecks(prev => prev.map(d => (d.id === id ? { ...d, name: clean } : d)))
    if (supabase && user) {
      const sb = supabase
      // The rename stays in state through a deck_busy wait; the rollback below
      // only runs if the retry fails too.
      const { data, error } = await runDeckWrite(() => sb
        .from('saved_decks')
        .update({ name: clean })
        .eq('id', id)
        .eq('user_id', user.id)
        .select('id'))
      if (error || !data || data.length === 0) {
        setDecks(prev => prev.map(d => (d.id === id ? { ...d, name: prevName } : d)))
        setCloudSyncError(error?.message ?? tc('saved_decks.rename_failed'))
      }
    }
  }, [isCloud, user, runDeckWrite, setDecks, mutateGuestDecks])

  // Unlink: null out the three link-state columns (source_url,
  // source_synced_at, source_decklist_hash) in one update. Deliberately does
  // NOT touch `source` (the platform name, e.g. 'archidekt') or `name` -
  // everything about the deck's own content and identity stays exactly as it
  // is; only the pointer back to the source deck and the bookkeeping around
  // it are cleared. Every other reader in this codebase treats `sourceUrl`
  // as the single "is this deck linked" flag, so clearing it alone is enough
  // to revert re-analyze to the stored-decklist path and drop the deck out of
  // "Refresh all linked (N)"'s count.
  // Mirrors renameDeck/reclassifyDeck exactly: optimistic local update in both
  // modes, with a cloud-mode rollback if the PATCH fails or hits zero rows.
  const unlinkDeck = useCallback(async (id: string) => {
    const prior = decksRef.current.find(d => d.id === id)
    if (!prior?.sourceUrl) return

    if (!isCloud) {
      const ok = await mutateGuestDecks(list => list.map(d => (
        d.id === id ? { ...d, sourceUrl: undefined, sourceSyncedAt: undefined, sourceDecklistHash: undefined } : d
      )))
      if (!ok) setCloudSyncError(tc('saved_decks.storage_full'))
      return
    }

    setDecks(prev => prev.map(d => (
      d.id === id ? { ...d, sourceUrl: undefined, sourceSyncedAt: undefined, sourceDecklistHash: undefined } : d
    )))
    if (supabase && user) {
      const sb = supabase
      const { data, error } = await runDeckWrite(() => sb
        .from('saved_decks')
        .update({ source_url: null, source_synced_at: null, source_decklist_hash: null })
        .eq('id', id)
        .eq('user_id', user.id)
        .select('id'))
      if (error || !data || data.length === 0) {
        setDecks(prev => prev.map(d => (
          d.id === id
            ? { ...d, sourceUrl: prior.sourceUrl, sourceSyncedAt: prior.sourceSyncedAt, sourceDecklistHash: prior.sourceDecklistHash }
            : d
        )))
        setCloudSyncError(error?.message ?? tc('saved_decks.unlink_failed'))
      }
    }
  }, [isCloud, user, runDeckWrite, setDecks, mutateGuestDecks])

  // Opt a deck in/out of the public Library. NOT optimistic: the caller
  // disables its toggle while this is pending, and local state updates only
  // after the PATCH is confirmed (a fire-and-forget flip + rollback can
  // resolve out of order against an opposing toggle). Returns success.
  const setLibraryListed = useCallback(async (id: string, listed: boolean): Promise<boolean> => {
    if (!isCloud || !supabase || !user) {
      setCloudSyncError(tc('saved_decks.sign_in_to_list'))
      return false
    }
    // .select('id') so an RLS-blocked / not-found PATCH surfaces as a failure
    // instead of a silent zero-row no-op.
    const sb = supabase
    const { data, error } = await runDeckWrite(() => sb
      .from('saved_decks')
      .update({ library_listed: listed })
      .eq('id', id)
      .eq('user_id', user.id)
      .select('id'))
    if (error || !data || data.length === 0) {
      setCloudSyncError(error?.message ?? tc('saved_decks.listing_failed'))
      return false
    }
    setDecks(prev => prev.map(d => (
      d.id === id
        // Listing is only reachable from the share modal, so listed=true
        // implies a live share link; unlisting leaves the link (isPublic) alone.
        ? { ...d, libraryListed: listed, ...(listed ? { isPublic: true } : {}) }
        : d
    )))
    return true
  }, [isCloud, user, runDeckWrite, setDecks])

  // Opt a deck on/off the owner's public creator page at /u/<token>.
  // Separate from the Library (2026-09-02): sharing a link for social media
  // or troubleshooting must not put a deck on a public identity page, which
  // is what one shared flag used to do. Same confirm-then-apply discipline
  // as setLibraryListed - see the comment there for why it is not optimistic.
  const setProfileFeatured = useCallback(async (id: string, featured: boolean): Promise<boolean> => {
    if (!isCloud || !supabase || !user) {
      setCloudSyncError(tc('saved_decks.sign_in_to_feature'))
      return false
    }
    // .select('id') so an RLS-blocked / not-found PATCH surfaces as a failure
    // instead of a silent zero-row no-op.
    const { data, error } = await supabase
      .from('saved_decks')
      .update({ profile_featured: featured })
      .eq('id', id)
      .eq('user_id', user.id)
      .select('id')
    if (error || !data || data.length === 0) {
      setCloudSyncError(error?.message ?? tc('saved_decks.feature_failed'))
      return false
    }
    setDecks(prev => prev.map(d => (
      d.id === id
        // Featuring is only reachable from the share modal, so featured=true
        // implies a live share link (the page links each deck by its token).
        // Un-featuring leaves the link (isPublic) alone.
        ? { ...d, profileFeatured: featured, ...(featured ? { isPublic: true } : {}) }
        : d
    )))
    return true
  }, [isCloud, user, setDecks])

  // Local-state-only sync for share-state changes made through the API
  // (/decks/share POST + revoke), which writes the DB out of band of this
  // hook. Keeps the "Listed in Library" and "Featured on your page"
  // indicators honest after a revoke.
  const applyShareState = useCallback((
    id: string,
    s: {
      isPublic?: boolean
      libraryListed?: boolean
      profileFeatured?: boolean
      publicShareToken?: string | null
    },
  ) => {
    setDecks(prev => prev.map(d => (
      d.id === id
        ? {
            ...d,
            ...(s.isPublic !== undefined ? { isPublic: s.isPublic } : {}),
            ...(s.libraryListed !== undefined ? { libraryListed: s.libraryListed } : {}),
            ...(s.profileFeatured !== undefined ? { profileFeatured: s.profileFeatured } : {}),
            ...(s.publicShareToken !== undefined ? { publicShareToken: s.publicShareToken } : {}),
          }
        : d
    )))
  }, [setDecks])

  // Resolves to the local id -> cloud id map of a migration that landed
  // (also recorded for Finish my deck's return to the same deck,
  // lib/finishReturn), or null when nothing moved.
  const migrateLocalDecks = useCallback(async (): Promise<Record<string, string> | null> => {
    if (!isCloud || !supabase || !user) return null

    const localDecks = loadLocalDecks()
    if (localDecks.length === 0) return null

    // Insert all local decks into Supabase. Must include the link columns
    // (name/source/source_url/source_synced_at/source_decklist_hash) - a
    // local deck imported from Archidekt while signed out carries all five,
    // and dropping them here would silently unname and unlink it on
    // migration, plus break the sourceUrl dedup so re-importing the same
    // deck later creates a duplicate row instead of updating this one.
    const rows = localDecks.map(d => ({
      user_id: user.id,
      commander: d.commander,
      partner: d.partner ?? null,
      bracket: d.bracket,
      engine: d.engine,
      card_count: d.cardCount,
      decklist: d.decklist ?? null,
      analysis: d.analysis as unknown as Record<string, unknown>,
      name: d.name ?? null,
      source: d.source ?? null,
      source_url: d.sourceUrl ?? null,
      source_synced_at: d.sourceSyncedAt ?? null,
      source_decklist_hash: d.sourceDecklistHash ?? null,
      // The guest's per-card printing / foil / tags come along (Wave 4).
      ...(storedCardMeta(d.cardMeta) ? { card_meta: storedCardMeta(d.cardMeta) } : {}),
    }))

    const sb = supabase
    const { data: inserted, error } = await runDeckWrite(() => sb.from('saved_decks').insert(rows).select('id'))

    if (!error) {
      // The editor drafts of the migrated decks follow them into the account
      // (new scope, new cloud id) BEFORE the guest rows go: a draft can be the
      // only copy of an edit. Without returned ids they stay as guest drafts.
      const ids = Array.isArray(inserted) ? (inserted as { id?: string }[]).map(r => r?.id) : []
      // Every migrated deck must come back with its id, and every divergent
      // draft must be copied; otherwise the guest rows and drafts stay and the
      // migration is offered again (a draft can be an edit's only copy).
      const allIds = ids.length === localDecks.length && ids.every(id => typeof id === 'string' && id.length > 0)
      let draftsCopied = allIds
      if (allIds) {
        for (let i = 0; i < localDecks.length; i++) {
          const d = localDecks[i]
          if (!(await rekeyMigratedDrafts(d.id, user.id, ids[i] as string, d.decklist ?? null))) draftsCopied = false
          // Text mode's unapplied text follows the deck the same way.
          if (!(await rekeyMigratedTextDrafts(d.id, user.id, ids[i] as string))) draftsCopied = false
        }
      }
      if (!draftsCopied) {
        console.error('Migration: decks inserted, but their ids or drafts could not be carried over; keeping the local copies.')
        return null
      }
      const moved: Record<string, string> = {}
      localDecks.forEach((d, i) => { moved[d.id] = ids[i] as string })
      recordMigratedIds(moved)
      // Clear local storage and mark as migrated
      localStorage.removeItem(STORAGE_KEY)
      localStorage.setItem(MIGRATION_KEY, 'true')
      setMigrationNeeded(false)

      // Reload cloud decks (owner-scoped, same as the main load path).
      const { data } = await supabase
        .from('saved_decks')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
      if (data) installLoadedDecks((data as DbRow[]).map(mapRowToDeck))
      // The migrated decks are this account's decks now.
      decksChangedRef.current(user.id)
      return moved
    } else {
      console.error('Migration failed:', error)
      return null
    }
  }, [isCloud, user, runDeckWrite, setDecks])

  const dismissMigration = useCallback(() => {
    localStorage.setItem(MIGRATION_KEY, 'true')
    setMigrationNeeded(false)
    // Also persist server-side via user_metadata so the next sign-in on
    // any browser doesn't re-show the prompt. Best-effort; if Supabase is
    // down the local flag still suppresses the prompt for this session.
    if (user && supabase) {
      void supabase.auth.updateUser({
        data: { saved_decks_migration_dismissed: true },
      }).catch((err) => {
        console.warn('Failed to persist migration dismissal:', err)
      })
    }
  }, [user])

  // Asked ONLY for the signed-in user's own rows (DecksPage passes it for a
  // saved deck, never for a Library or precon one), and only DeckDetailV2's
  // silent refresh acts on it, which never writes a read-only deck.
  const needsReanalysis = useCallback((deck: SavedDeck): boolean => {
    // An own deck analyzed in another language than the interface is
    // re-analyzed in the viewer's language (i18n 3e.2, Keith's decision Q1).
    // The marker is the API's `analysis_locale`; absent means English. With
    // the flag off the interface is always English, so a deck saved under a
    // Spanish interface re-analyzes in English after a rollback.
    if (isAnalysisInOtherLanguage(deck.analysis)) return true
    // If analysis data is present and complete, don't force reanalysis just because
    // the version string is missing (older saves before versioning was added)
    if (!deck.analysisVersion) {
      const hasAnalysis = deck.analysis?.bracket_analysis && deck.analysis?.ipom_analysis
      return !hasAnalysis
    }
    // Equivalence-aware check so future pure-metadata server schema bumps
    // (e.g. BRACKETS_SCHEMA_VERSION 5.1.0 -> 5.2.0) don't force a re-analyze
    // on every saved deck. See src/api/analysisVersion.ts.
    return !isAnalysisVersionCompatible(deck.analysisVersion)
  }, [])

  return {
    decks,
    loading,
    saveDeck,
    saveDraftDeck,
    currentScope,
    saveDeckChecked,
    applyPersistedDeck,
    removeDeck,
    reclassifyDeck,
    renameDeck,
    refreshDeck,
    /**
     * This hook's own auth read has settled, and for whom (null = guest). A
     * caller gating on ITS useAuth must also wait for these to agree:
     * useAuth() state is per instance, so the two settle independently.
     */
    authSettled: !authLoading,
    ownerId: user?.id ?? null,
    // Offline (W3): the list is this device's stored copy from this date
    // ('' when its date is unknown); null for a fresh read.
    offlineAsOf,
    readDeckBase,
    confirmRefreshLanded,
    unlinkDeck,
    setLibraryListed,
    setProfileFeatured,
    applyShareState,
    isCloud,
    migrationNeeded,
    migrateLocalDecks,
    dismissMigration,
    needsReanalysis,
    cloudSyncError,
    clearCloudSyncError: () => setCloudSyncError(null),
    // True only while a deck_busy retry is pending. NOT an error state: the
    // edit is still here and the write is coming back.
    savingBusy,
    // Test / debugging seam: how many rows have a content save still being
    // tracked for rollback. Settled saves, removed rows and adopted temp ids
    // never linger here.
    trackedContentSaves: () => contentAttemptRef.current.size,
  }
}
